import React, { useEffect, useState, useRef } from "react";
import * as d3 from "d3";
import {
  GetQuestionDataAllLocations,
  GetSubQuestionAllLocations,
  GetPatternsOverTime,
  GetLocations,
} from "../Util/Api";
import DataVisualizationToggle from "./DataVisualizationToggle";
import { topicDictionary } from "../Util/tileDictionary";
import { EnableSubQuestionTextMatching } from "../Util/env";
import { isCensusSite } from "../Util/SuppressionRules";
import {
  selectGraphAllValue,
  selectGraphCompare,
} from "../Util/Analytics/InteractionAnalytics";

// Chart titles are rendered inside the SVGs; external title/toggle removed
const ChartContainer = ({ children }) => (
  <div style={{ padding: 20 }} className="graph-container">
    {children}
  </div>
);

const RadioGroup = ({ label, name, options, value, onChange }) => (
  <div style={{ marginBottom: 8 }} className="graph-radio-group">
    <label style={{ marginRight: 8, fontWeight: 600 }}>{label}:</label>
    {options.map((opt) => (
      <label key={opt.value} style={{ marginRight: 12 }}>
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          style={{ marginRight: 4 }}
        />
        <span>{opt.label}</span>
      </label>
    ))}
  </div>
);

const Graph = ({
  location,
  onVizTypeChange,
  question,
  subQuestion,
  vizType,
  subQuestionValue,
  locationAbbr,
  year,
  setGraphCsvData,
}) => {
  const [loading, setLoading] = useState(false);
  const [allRange, setAllRange] = useState(null);
  const [localRange, setLocalRange] = useState(null);
  // Track if the requested year has data available for this location
  // For specific locations, default to null until we confirm the year has data
  const [requestedYearHasData, setRequestedYearHasData] = useState(null);
  // Track whether we have suppressed 0–100 CI ranges to display footnote
  // eslint-disable-next-line no-unused-vars
  const [hasSuppressionFootnote, setHasSuppressionFootnote] = useState(false);

  // UI state
  const [allMode, setAllMode] = useState("percent"); // percent | ci
  const [compareMode, setCompareMode] = useState("None");
  const [compareNotice, setCompareNotice] = useState("");

  const svgRef = useRef();
  const svgLocalRef = useRef();
  const svgPatternRefs = useRef([]);

  // Data/state used across rendering paths
  const [allLocationsData, setAllLocationsData] = useState(null);
  const [patternsData, setPatternsData] = useState(null);
  const [patternsMap, setPatternsMap] = useState({});
  const [compareData, setCompareData] = useState(null);
  const [compareDataBySubQuestion, setCompareDataBySubQuestion] = useState({});

  // Update page title based on location (mirrors Table.js behavior)
  useEffect(() => {
    if (location) {
      document.title = `School Health Profiles Explorer | 
      ${location.LocationDesc + " Results"} 
      `;
    }
  }, [location]);

  const isAll = !location || location.LocationAbbr === "ALL" || location.LocationType === "ALL";
  const isState = location && location.LocationType === "State";
  const isLocal = location && location.LocationType === "Local";

  // Visualization colors used across render and JSX
  // Match graph primary color to the selected topic color when available
  const topicColor = (question && question.TopicId && topicDictionary[question.TopicId] && topicDictionary[question.TopicId].color) || "#066194";
  const localColor = topicColor; // primary series color (lines/bars)
  const allBarColor = topicColor; // All view bars should also reflect topic color
  const getCompareColor = () => (compareMode && compareMode !== "None") ? "#8a8a8a" : "#d95f02";
  const HIGH_VALUE_THRESHOLD = 97; // move labels inside bar when value is near 100

  /**
   * Suppression Rules (aligned with updated requirements):
   * - DataValue missing (null/undefined/"") → Hide location
   * - DataValue is 111111, 333333, or 555555 → Hide location
   * - DataValue is 0 → Hide location
   * - DataValue is 100 → Display location; no CI
   * - Census sites → Display location; no CI
   */

  // Special data-value codes where values should be suppressed
  function isSpecialDataCode(v) {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    return s === "111111" || s === "333333" || s === "555555";
  }

  useEffect(() => {
    if (!question) return;
    setLoading(true);
    // Fetch appropriate data based on selection (graph-only scope for PatternsOverTime)
    (async () => {
      try {
        if (isAll) {
          // Use the selected year prop, or fall back to the question's default year
          const fetchYear = year || question.LatestYear || question.DefaultYear || null;
          
          let resp;
          if (subQuestion && subQuestion.SubQuestionId) {
            // Use SubQuestion all-locations endpoint when a subQuestion is selected
            resp = await GetSubQuestionAllLocations({ subQuestionId: subQuestion.SubQuestionId, questionId: question.QuestionId, year: fetchYear });
          } else {
            resp = await GetQuestionDataAllLocations({ questionId: question.QuestionId, year: fetchYear });
          }
          const payload = resp && resp.data ? resp.data : resp;
          const filteredPayload = filterDataByTopic(payload);
          setAllLocationsData(filteredPayload);

          // compute range for Local Surveys (min and max percent across localities)
          try {
            const src = Array.isArray(filteredPayload) ? filteredPayload : filteredPayload && filteredPayload.tableData ? filteredPayload.tableData : [];
            const localItemsForRange = src.filter((s) => (s.LocationType || "").toLowerCase() === "local");
            const localPercents = localItemsForRange
              .filter((d) => {
                const val = d.NPercent ?? d.DataValueRaw;
                return !isSpecialDataCode(val);
              })
              .map((d) => parseNumber(d.NPercent) ?? parseNumber(d.DataValueRaw))
              .filter((n) => n !== null && !isNaN(n) && n !== 0 && n < 100.1); // Additional filter: exclude values > 100 and hidden 0 values
            if (localPercents.length > 0) {
              const mnl = Math.min(...localPercents);
              const mxl = Math.max(...localPercents);
              const minStr = mnl === 0 ? "0" : mnl === 100 ? "100" : mnl.toFixed(1);
              const maxStr = mxl === 0 ? "0" : mxl === 100 ? "100" : mxl.toFixed(1);
              setLocalRange({ min: minStr, max: maxStr });
            } else {
              setLocalRange(null);
            }
          } catch (e) {
            setLocalRange(null);
          }

          // compute range for State Surveys (min and max percent across states)
          try {
            const source = Array.isArray(filteredPayload) ? filteredPayload : filteredPayload && filteredPayload.tableData ? filteredPayload.tableData : [];
            const stateItems = source.filter((s) => (s.LocationType || "").toLowerCase() === "state");
            const percents = stateItems
              .filter((d) => {
                const val = d.NPercent ?? d.DataValueRaw;
                return !isSpecialDataCode(val);
              })
              .map((d) => parseNumber(d.NPercent) ?? parseNumber(d.DataValueRaw))
              .filter((n) => n !== null && !isNaN(n) && n !== 0 && n < 100.1); // Additional filter: exclude values > 100 and hidden 0 values
            if (percents.length > 0) {
              const mn = Math.min(...percents);
              const mx = Math.max(...percents);
              const minStr = mn === 0 ? "0" : mn === 100 ? "100" : mn.toFixed(1);
              const maxStr = mx === 0 ? "0" : mx === 100 ? "100" : mx.toFixed(1);
              setAllRange({ min: minStr, max: maxStr });
            } else {
              setAllRange(null);
            }
          } catch (e) {
            // ignore
            setAllRange(null);
          }
        } else {
          // Patterns over time for the selected location
          // If the question has multiple subquestions and no single subQuestion prop
          // is selected, fetch PatternsOverTime for each subquestion and store
          // results in `patternsMap`. Otherwise fetch a single series into
          // `patternsData` as before.
          

          if (question && question.SubQuestions && question.SubQuestions.length > 0 && !subQuestion) {
            // clear single-pattern state
            setPatternsData(null);
            setPatternsMap({});
            
            // fetch each subquestion series in parallel
            await Promise.all(
              question.SubQuestions.map(async (sq, index) => {
                try {
                  const resp = await GetPatternsOverTime({
                    locationAbbr: location.LocationAbbr,
                    questionId: question.QuestionId,
                    subQuestionId: "",
                    subQuestionText: sq.SubQuestion || "",
                    // When no subquestion text, fall back to SummaryText
                    summaryText: question ? question.SummaryText : "",
                    matchSubQuestionByText: EnableSubQuestionTextMatching,
                  });
                  const data = resp && resp.data ? resp.data : resp;
                  let filtered = filterRowsByTopic(normalizeRows(data));
                  // Filter to only rows matching this specific subquestion text (backend may return all subquestions)
                  const targetText = sq.SubQuestion ? String(sq.SubQuestion).toLowerCase().trim() : null;
                  if (targetText) {
                    filtered = filtered.filter(row => {
                      const rowText = row.SubQuestion || row.SubQuestionText || row.subQuestion || "";
                      return String(rowText).toLowerCase().trim() === targetText;
                    });
                  }
                  const deduplicated = deduplicateByYear(filtered, sq.SubQuestionId);
                  setPatternsMap((prev) => ({ ...prev, [sq.SubQuestionId]: deduplicated }));
                } catch (e) {
                  console.error("Error fetching subquestion patterns", sq && sq.SubQuestionId, e);
                }
              })
            );
          } else {
            const resp = await GetPatternsOverTime({
              locationAbbr: location.LocationAbbr,
              questionId: question.QuestionId,
              subQuestionId: "",
              subQuestionText: subQuestion ? subQuestion.SubQuestion : "",
              // When no subquestion text, fall back to SummaryText
              summaryText: question ? question.SummaryText : "",
              matchSubQuestionByText: EnableSubQuestionTextMatching,
            });

            const data = resp && resp.data ? resp.data : resp;
            const filtered = filterDataByTopic(data);
            const normalized = normalizeRows(filtered);
            const deduplicated = deduplicateByYear(normalized, subQuestion ? subQuestion.SubQuestionId : null);
            // Preserve original structure if it was an object
            const finalData = Array.isArray(filtered) ? deduplicated : { ...filtered, tableData: deduplicated };
            setPatternsData(finalData);
            setPatternsMap({});
          }
        }
      } catch (e) {
        console.error("Graph data load error", e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, subQuestion, location, isAll, year]);

  // Validate that patterns data exists for a specific location.
  // For PatternsOverTime, we display whatever years are available rather than
  // requiring a specific year to exist. Only block display when there's no data at all.
  useEffect(() => {
    // Only validate for specific locations (not ALL)
    if (isAll) {
      setRequestedYearHasData(true);
      return;
    }

    // If patterns data hasn't loaded yet, assume it will have data
    if (!patternsData && Object.keys(patternsMap).length === 0) {
      setRequestedYearHasData(true);
      return;
    }

    // Check if we have ANY data (not requiring a specific year)
    let hasData = false;
    
    // Check single patterns data
    if (patternsData) {
      let dataArray = [];
      if (Array.isArray(patternsData)) {
        dataArray = patternsData;
      } else if (patternsData && typeof patternsData === 'object') {
        dataArray = patternsData.tableData || patternsData.data || [];
      }
      hasData = dataArray.length > 0;
    }
    
    // Check multi-subquestion patterns map
    if (!hasData && Object.keys(patternsMap).length > 0) {
      hasData = Object.values(patternsMap).some(data => {
        const arr = Array.isArray(data) ? data : (data?.tableData || []);
        return arr.length > 0;
      });
    }
    
    setRequestedYearHasData(hasData);
  }, [patternsData, patternsMap, isAll]);

  useEffect(() => {
    // Render chart whenever data or UI controls change
    renderChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLocationsData, patternsData, patternsMap, allMode, compareMode, loading, compareData, compareDataBySubQuestion, question]);

  // Fetch compare series when compareMode changes (State/Local views)
  useEffect(() => {
    if (!question || (!isState && !isLocal)) {
      setCompareData(null);
      setCompareNotice("");
      return;
    }

    const fetchCompare = async () => {
      setCompareNotice("");
      if (compareMode === "None") {
        setCompareData(null);
        return;
      }

      try {
        let compareAbbr = null;
        if (compareMode === "Nationwide") {
          compareAbbr = "ALL"; // API convention: ALL for nationwide
        } else if (compareMode === "State") {
          if (location.ParentLocationAbbr) compareAbbr = location.ParentLocationAbbr;
          else if (location.ParentLocationId) {
            const locResp = await GetLocations();
            const locs = locResp && locResp.data ? locResp.data : locResp;
            const parent = (locs || []).find((l) => String(l.LocationId) === String(location.ParentLocationId));
            if (parent) compareAbbr = parent.LocationAbbr;
          } else if (location.ParentLocationDesc) {
            const locResp = await GetLocations();
            const locs = locResp && locResp.data ? locResp.data : locResp;
            const parent = (locs || []).find((l) => l.LocationDesc === location.ParentLocationDesc || l.LocationTxt === location.ParentLocationDesc);
            if (parent) compareAbbr = parent.LocationAbbr;
          }
          // If we still don't have an abbr, try a fuzzy match on location text
          if (!compareAbbr && location.ParentLocationDesc) {
            try {
              const locResp2 = await GetLocations();
              const locs2 = locResp2 && locResp2.data ? locResp2.data : locResp2;
              if (locs2 && locs2.length > 0) {
                const needle = String(location.ParentLocationDesc).toLowerCase();
                const parentFuzzy = locs2.find((l) => {
                  const d = (l.LocationDesc || l.LocationTxt || "").toString().toLowerCase();
                  return d.includes(needle) || needle.includes(d);
                });
                if (parentFuzzy) compareAbbr = parentFuzzy.LocationAbbr;
              }
            } catch (e) {
              // ignore fuzzy lookup failures
            }
          }
        }

        if (!compareAbbr) {
          setCompareData(null);
          setCompareNotice(compareMode === "State" ? "State comparison is unavailable for this district." : "Comparison data is unavailable for this selection.");
          return;
        }

        // debug: log which compare abbr we're fetching
        const resp = await GetPatternsOverTime({
          locationAbbr: compareAbbr,
          questionId: question.QuestionId,
          subQuestionId: "",
          subQuestionText: subQuestion ? subQuestion.SubQuestion : "",
          summaryText: question ? question.SummaryText : "",
          matchSubQuestionByText: EnableSubQuestionTextMatching,
        });
        let data = filterDataByTopic(resp && resp.data ? resp.data : resp);
        // Deduplicate by year only when a specific subquestion is selected.
        // In multi-subquestion mode, keep all subquestions so compare renders for each chart.
        const normalizedData = normalizeRows(data);
        const deduplicatedData = subQuestion
          ? deduplicateByYear(normalizedData, subQuestion.SubQuestionId, subQuestion.SubQuestion)
          : normalizedData;
        data = Array.isArray(data) ? deduplicatedData : { ...data, tableData: deduplicatedData };

        const finalRows = Array.isArray(data) ? data.length : (data && data.tableData ? data.tableData.length : 0);
        if (finalRows === 0) {
          setCompareData(null);
          const scope = compareMode === "State" ? "state" : "nationwide";
          setCompareNotice(`No ${scope} data available for this question/year.`);
        } else {
          setCompareData({ abbr: compareAbbr, data });
        }
      } catch (e) {
        console.error("Compare data load error", e);
        setCompareData(null);
        setCompareNotice("Unable to load comparison data right now.");
      }
    };

    fetchCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, question, subQuestion, location, isState, isLocal]);

  // Fetch per-subquestion compare data for multi-subquestion (ALL) mode with Nationwide comparison
  useEffect(() => {
    if (!question || !question.SubQuestions || question.SubQuestions.length === 0 || subQuestion) {
      // Not in multi-subquestion mode
      setCompareDataBySubQuestion({});
      return;
    }

    if (compareMode !== "Nationwide" || (!isState && !isLocal)) {
      setCompareDataBySubQuestion({});
      return;
    }

    const fetchPerSubQuestionCompare = async () => {
      const result = {};
      try {
        // Fetch Nationwide compare data for each subquestion in parallel
        await Promise.all(
          (question.SubQuestions || []).map(async (sq) => {
            try {
              const resp = await GetPatternsOverTime({
                locationAbbr: "ALL",
                questionId: question.QuestionId,
                subQuestionId: "",
                subQuestionText: sq.SubQuestion || "",
                summaryText: question ? question.SummaryText : "",
                matchSubQuestionByText: EnableSubQuestionTextMatching,
              });
              const data = resp && resp.data ? resp.data : resp;
              const filtered = filterDataByTopic(data);
              const normalized = normalizeRows(filtered);
              const deduplicated = deduplicateByYear(normalized, sq.SubQuestionId, sq.SubQuestion);

              if (deduplicated && deduplicated.length > 0) {
                result[sq.SubQuestionId] = { abbr: "ALL", data: deduplicated };
              }
            } catch (e) {
              // Skip this subquestion if it errors
            }
          })
        );
        setCompareDataBySubQuestion(result);
      } catch (_) {
        setCompareDataBySubQuestion({});
      }
    };

    fetchPerSubQuestionCompare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, question?.QuestionId, patternsMap, isState, isLocal]);

  useEffect(() => {
    if (!setGraphCsvData) return;

    if (isAll) {
      setGraphCsvData(null);
      return;
    }

    let baseRows = normalizeRows(patternsData);
    const hasSubQuestion = !!subQuestion;
    if ((!baseRows || baseRows.length === 0) && patternsMap && Object.keys(patternsMap).length > 0) {
      baseRows = Object.values(patternsMap).flatMap((d) => normalizeRows(d));
    }

    let compareRows = [];
    if (compareMode && compareMode !== "None") {
      if (compareMode === "Nationwide" && !hasSubQuestion && compareDataBySubQuestion && Object.keys(compareDataBySubQuestion).length > 0) {
        compareRows = Object.values(compareDataBySubQuestion).flatMap((entry) => normalizeRows(entry.data));
      } else if (compareData && compareData.data) {
        compareRows = normalizeRows(compareData.data);
      }
    }

    const combined = [...(baseRows || []), ...(compareRows || [])].filter(Boolean);
    const deduped = new Map();
    combined.forEach((row) => {
      const yr = row.Yr ?? row.Year ?? "";
      const locAbbr = row.LocationAbbr || row.Location?.Abbr || "";
      const subq = row.SubQuestionId || row.SubQuestion || "";
      const key = `${yr}::${locAbbr}::${subq}`;
      const prev = deduped.get(key);
      if (!prev || Object.keys(row).length > Object.keys(prev).length) {
        deduped.set(key, row);
      }
    });

    const finalRows = Array.from(deduped.values());
    setGraphCsvData(finalRows.length > 0 ? finalRows : null);
  }, [compareMode, isAll, patternsData, patternsMap, compareData, compareDataBySubQuestion, setGraphCsvData, subQuestion]);

  function parseNumber(v) {
    if (v === null || v === undefined) return null;
    const n = Number(v);
    if (!isNaN(n)) return n; // Return the parsed number if valid
    // Try to parse ranges like "(3.4-6.2)" or "3.4 - 6.2"
    const m = String(v).match(/([0-9]+\.?[0-9]*)[^0-9]+([0-9]+\.?[0-9]*)/);
    if (m) return (Number(m[1]) + Number(m[2])) / 2;
    // Fallback: extract first numeric token (handles values like "100%" or "~7")
    const first = String(v).match(/[+-]?\d*\.?\d+/);
    if (first && first[0] !== "") {
      const f = Number(first[0]);
      if (!isNaN(f)) return f;
    }
    return null;
  }

  // Determine whether a value should be suppressed (special codes only: 111111, 333333, 555555)
  function isSuppressedValue(row) {
    if (!row) return false;
    // Try each field in order, checking for null/undefined (not falsy, so 0 is valid)
    let value;
    if (row.NPercent !== null && row.NPercent !== undefined && row.NPercent !== '') {
      value = row.NPercent;
    } else if (row.DataValue !== null && row.DataValue !== undefined && row.DataValue !== '') {
      value = row.DataValue;
    } else if (row.DataValueRaw !== null && row.DataValueRaw !== undefined && row.DataValueRaw !== '') {
      value = row.DataValueRaw;
    } else {
      value = null;
    }
    if (value === null || value === undefined || value === "") return false;
    return isSpecialDataCode(value);
  }

  // Return a numeric percent value unless it is suppressed
  function getPercentValue(row) {
    if (!row || isSuppressedValue(row)) return null;
    // Get the raw value, checking each field explicitly to handle 0 correctly
    // Empty strings should be treated as missing data (null), not converted to 0
    let raw;
    if (row.NPercent !== null && row.NPercent !== undefined && row.NPercent !== '') {
      raw = row.NPercent;
    } else if (row.DataValue !== null && row.DataValue !== undefined && row.DataValue !== '') {
      raw = row.DataValue;
    } else if (row.DataValueRaw !== null && row.DataValueRaw !== undefined && row.DataValueRaw !== '') {
      raw = row.DataValueRaw;
    } else {
      return null;
    }
    const num = parseNumber(raw);
    if (num === null || isNaN(num)) return null;
    if (num === 0) return null;
    return num;
  }

  // Detect missing CI bounds for a row (no usable lower/upper values)
  function hasCIBounds(row) {
    if (!row) return false;
    const candidatesLower = [row.LowValue, row.LowerValue, parseConfidenceLower(row.ConfidenceInterval), row.LCI, row.Lower, row.LowCI];
    const candidatesUpper = [row.HighValue, row.UpperValue, parseConfidenceUpper(row.ConfidenceInterval), row.UCI, row.Upper, row.HighCI];
    let lower = null, upper = null;
    for (let c of candidatesLower) {
      if (c !== undefined && c !== null && String(c).trim() !== '') {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { lower = n; break; }
      }
    }
    for (let c of candidatesUpper) {
      if (c !== undefined && c !== null && String(c).trim() !== '') {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { upper = n; break; }
      }
    }
    if (lower === null || upper === null) return false;
    // Treat degenerate 0 or 100 CIs as missing for suppression rendering
    if (lower === upper && (lower === 0 || lower === 100)) return false;
    return true;
  }

  // Dagger no longer used.

  // Show an em dash when CI data are missing for non-suppressed, non-0/100 values
  function shouldShowDashForMissingCI(row) {
    if (!row || isSuppressedValue(row)) return false;
    if (isCensusSite(row)) return false;
    const val = getPercentValue(row);
    if (val === null || val === undefined) return false;
    if (val === 100) return false;
    if (hasCIBounds(row)) return false;

    // Treat blank or dash CI strings as missing; other strings with numbers would have been parsed above
    const ciStr = row.ConfidenceInterval;
    if (ciStr !== undefined && ciStr !== null) {
      const s = String(ciStr).trim();
      if (s !== "" && s !== "—" && s !== "-" && s !== "--" && s !== "–") {
        return false;
      }
    }

    // Track for centralized footnote rendering
    setHasSuppressionFootnote(true);
    return true;
  }

  // Check if CI should display as a dash (CI string is dash/empty, or bounds missing/incomplete)
  function shouldShowCIDash(row) {
    if (!row) return false;
    if (isCensusSite(row)) return false;
    const val = getPercentValue(row);
    if (val === null || val === undefined) return false;
    if (val === 100) return false;
    
    // Check the ConfidenceInterval string first (like table does)
    const ciStr = row.ConfidenceInterval;
    if (ciStr !== undefined && ciStr !== null) {
      const s = String(ciStr).trim();
      // If CI string is empty or a dash, show dash
      if (s === "" || s === "—" || s === "-" || s === "--" || s === "–") {
        setHasSuppressionFootnote(true);
        return true;
      }
    }
    
    // Check if bounds are missing/incomplete
    const candidatesLower = [row.LowValue, row.LowerValue, parseConfidenceLower(row.ConfidenceInterval), row.LCI, row.Lower, row.LowCI];
    const candidatesUpper = [row.HighValue, row.UpperValue, parseConfidenceUpper(row.ConfidenceInterval), row.UCI, row.Upper, row.HighCI];
    let lower = null, upper = null;
    for (let c of candidatesLower) {
      if (c !== undefined && c !== null && String(c).trim() !== '') {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { lower = n; break; }
      }
    }
    for (let c of candidatesUpper) {
      if (c !== undefined && c !== null && String(c).trim() !== '') {
        const n = parseNumber(c);
        if (n !== null && !isNaN(n)) { upper = n; break; }
      }
    }
    
    // If either bound is missing, show dash
    if (lower === null || upper === null) {
      setHasSuppressionFootnote(true);
      return true;
    }
    
    return false;
  }

  // Format percent text for labels; show em-dash when missing, dagger handled earlier
  // Show 0 and 100 without decimals, other values with 1 decimal place
  function formatPercentLabel(row) {
    const val = getPercentValue(row);
    // Explicitly handle 0 since it can be falsy in some contexts
    if (val === null || val === undefined) return "—";
    if (val === 100) return "100";
    return d3.format('.1f')(val);
  }

  // Variant used for compare series where we still want the value even if CI is missing
  function formatPercentLabelAllowMissingCI(row) {
    const val = getPercentValue(row);
    if (val === null || val === undefined) return "—";
    if (val === 100) return "100";
    return d3.format('.1f')(val);
  }

  // Helper to format a single percent value (for CI ranges)
  function formatPercent(val) {
    if (val === 0) return "0";
    if (val === 100) return "100";
    return d3.format('.1f')(val);
  }

  // Some subquestions are reused across multiple topics. When that happens,
  // the API may return rows for every topic that shares the SubQuestionId,
  // which surfaces incorrect years on the charts. Filter the payload by the
  // currently selected topic when the topic id is present on a row.
  function extractTopicId(row) {
    if (!row || typeof row !== "object") return null;
    const candidates = [
      row.TopicId,
      row.TopicID,
      row.topicId,
      row.topicID,
      row.Topicid,
      row.topicid,
    ];
    for (const c of candidates) {
      if (c !== undefined && c !== null && String(c).trim() !== "") {
        return String(c).trim();
      }
    }
    return null;
  }

  function filterRowsByTopic(rows) {
    if (!Array.isArray(rows)) return rows || [];
    const topicId = question && question.TopicId ? String(question.TopicId).toLowerCase() : null;
    if (!topicId) return rows;
    return rows.filter((r) => {
      const rowTopic = extractTopicId(r);
      if (!rowTopic) return true; // keep rows when topic metadata is absent
      return String(rowTopic).toLowerCase() === topicId;
    });
  }

  function filterDataByTopic(data) {
    if (!question || !question.TopicId) return data;
    if (Array.isArray(data)) return filterRowsByTopic(data);
    if (data && Array.isArray(data.tableData)) {
      return { ...data, tableData: filterRowsByTopic(data.tableData) };
    }
    return data;
  }

  function normalizeRows(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.tableData)) return data.tableData;
    return [];
  }

  // Deduplicate PatternsOverTime data by year (backend may return multiple SubQuestionIds with same text)
  // Keep only one row per year, preferring rows that match the current subQuestion if available
  function normalizeText(s) {
    if (s === null || s === undefined) return "";
    return String(s).toLowerCase().replace(/\s+/g, " ").trim();
  }

  function deduplicateByYear(rows, preferredSubQuestionId = null, preferredText = null) {
    if (!rows || rows.length === 0) return rows;
    const byYear = {};
    rows.forEach(row => {
      const yr = row.Yr || row.Year;
      if (yr === undefined || yr === null) return;
      const key = String(yr);
      const current = byYear[key];
      if (!current) {
        byYear[key] = row;
      } else {
        const matchesPreferredId = preferredSubQuestionId && row.SubQuestionId === preferredSubQuestionId;
        const matchesPreferredText = preferredText && normalizeText(row.SubQuestion || row.SubQuestionText || row.subQuestion) === normalizeText(preferredText);
        const currentMatchesPreferredId = preferredSubQuestionId && current.SubQuestionId === preferredSubQuestionId;
        const currentMatchesPreferredText = preferredText && normalizeText(current.SubQuestion || current.SubQuestionText || current.subQuestion) === normalizeText(preferredText);

        // Prefer a row that matches preferred ID or text if the current one doesn't
        if (matchesPreferredId && !currentMatchesPreferredId) {
          byYear[key] = row;
        } else if (!currentMatchesPreferredId && matchesPreferredText && !currentMatchesPreferredText) {
          byYear[key] = row;
        }
        // Otherwise keep the first
      }
    });
    return Object.values(byYear).sort((a, b) => (a.Yr || a.Year) - (b.Yr || b.Year));
  }

  function clearSvg() {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();
    if (svgLocalRef && svgLocalRef.current) { // Check if local SVG reference exists
      const svg2 = d3.select(svgLocalRef.current);
      svg2.selectAll("*").remove();
    }
    // clear pattern svgs
    if (svgPatternRefs && svgPatternRefs.current && svgPatternRefs.current.length > 0) {
      svgPatternRefs.current.forEach((el) => {
        try {
          if (el) d3.select(el).selectAll("*").remove();
        } catch (e) {
          // ignore
        }
      });
    }
  }

  function renderChart() {
    clearSvg();
    const svg = d3.select(svgRef.current);
    const width = Math.max(300, (svgRef.current && svgRef.current.clientWidth) || 600);
    // default height for non-All views
    let svgHeight = 360;

    if (loading) {
      svg.append("text").attr("x", 20).attr("y", 40).text("Loading...");
      return;
    }

    // use `localColor` and `getCompareColor()` defined in component scope

    if (isAll) {
      const source = Array.isArray(allLocationsData) ? allLocationsData : allLocationsData && allLocationsData.tableData ? allLocationsData.tableData : [];
      if (!source || source.length === 0) {
        svg.append("text").attr("x", 20).attr("y", 40).text("No data available");
        return;
      }

      // Build list: Nationwide first, then all States alphabetically
      const nationwideItem = source.find(
        (s) => (s.LocationAlias && String(s.LocationAlias).toLowerCase() === "national") ||
               (s.LocationTxt && String(s.LocationTxt).toLowerCase() === "nationwide") ||
               (s.LocationType && String(s.LocationType).toLowerCase() === "national")
      );
      const stateItems = source
        .filter((s) => (s.LocationType && String(s.LocationType).toLowerCase() === "state"))
        .filter((s) => !isSuppressedValue(s))
        // Hide locations with missing or exactly 0% values; always allow 100%
        .filter((s) => {
          const v = getPercentValue(s);
          return v !== null && v !== 0; // keep any non-null, non-zero values (100 stays)
        })
        // When in CI mode, exclude locations without valid CI data (N/A)
        .filter((s) => {
          if (allMode === "ci") {
            return computeCIRange(s) !== null;
          }
          return true;
        })
        .slice()
        .sort((a, b) => ((a.LocationTxt || a.LocationDesc || "").localeCompare(b.LocationTxt || b.LocationDesc || "")));
      const items = nationwideItem ? [nationwideItem, ...stateItems] : stateItems;

      const margin = { top: 0, right: 20, bottom: 40, left: 220 };
      const innerWidth = width - margin.left - margin.right;
      // responsive per-row height
      const perRowHeight = innerWidth < 600 ? 56 : 40; // px per row for state rows
      // determine Nationwide height (reduced by ~15% to reduce visual weight)
      const nationwideHeight = Math.max( Math.min(Math.round(perRowHeight * 0.45), 48), 24 );
      // compute innerHeight based on state rows + nationwide slot
      const stateCount = stateItems.length;
      const innerHeight = Math.max(160, nationwideHeight + stateCount * perRowHeight);
      svgHeight = innerHeight + margin.top + margin.bottom + 20;

      const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

      // Keep numeric labels vertically centered on their bars and adjust horizontally when they overlap CI spans
      const resolveCIOverlap = (txt, bbox, lx, ux, barRightX, chartWidth, marginShift = 6) => {
        if (lx === null || ux === null || !bbox) return;
        const labelX = Number(txt.attr("x"));
        const anchor = txt.attr("text-anchor") || "start";
        const labelStart = anchor === "end" ? labelX - bbox.width : labelX;
        const labelEnd = anchor === "end" ? labelX : labelX + bbox.width;
        const ciMin = Math.min(lx, ux);
        const ciMax = Math.max(lx, ux);
        const overlapsCI = !(labelEnd < ciMin || labelStart > ciMax);
        if (!overlapsCI) return;

        // Prefer pushing the label just past the CI upper bound (and bar edge) on the right
        const pushRight = Math.max(ciMax + marginShift, barRightX + marginShift);
        if (pushRight + bbox.width <= chartWidth) {
          txt.attr("x", pushRight).attr("text-anchor", "start").style("fill", "#000");
          return;
        }

        // Otherwise push to the left of the CI (staying near the bar) and keep contrast
        const pushLeft = Math.min(ciMin - marginShift, barRightX - marginShift);
        txt.attr("x", pushLeft).attr("text-anchor", "end");
        const insideBar = pushLeft <= barRightX;
        txt.style("fill", insideBar ? "#fff" : "#000");
      };

      // responsive extra space between top axis and first bar (smaller on narrow screens)
      const topPaddingBetweenAxisAndFirstBar = innerWidth < 600 ? 20 : 30; // px
      const x = d3.scaleLinear().domain([0, 100]).range([0, innerWidth]);

      // x axis with quartile ticks - render at top
      const xAxis = d3.axisTop(x).tickValues([0, 25, 50, 75, 100]).tickFormat((d) => `${d}`).tickSize(0);
      // position the axis at a fixed top position
      const axisY = topPaddingBetweenAxisAndFirstBar;
      const firstBarY = axisY + 24; // start first bar 24px below axis line to match Local Surveys spacing
      const topAxisG = g.append("g").attr("transform", `translate(0, ${axisY})`).call(xAxis);
      topAxisG.selectAll("text").style("font-size", "14px").attr("dy", "-0.5em");
      topAxisG.selectAll("path").style("stroke", "#000").style("stroke-width", "2px").style("stroke-linecap", "square");
      topAxisG.selectAll("line").remove();
      // add matching x-axis at the bottom so labels appear at top and bottom
      const bottomAxisG = g.append("g").attr("transform", `translate(0, ${innerHeight})`).call(d3.axisBottom(x).tickValues([0, 25, 50, 75, 100]).tickFormat((d) => `${d}`).tickSize(0));
      bottomAxisG.selectAll("text").style("font-size", "14px").attr("dy", "1.2em");
      bottomAxisG.selectAll("path").style("stroke", "#000").style("stroke-width", "2px").style("stroke-linecap", "square");
      bottomAxisG.selectAll("line").remove();

      // vertical grid lines at x tick positions (light gray), behind bars
      const xTicks = [0, 25, 50, 75, 100];
      g.selectAll("line.vgrid").data(xTicks).join("line")
        .attr("class", "vgrid")
        .attr("x1", (d) => x(d))
        .attr("x2", (d) => x(d))
        .attr("y1", firstBarY)
        .attr("y2", innerHeight)
        .attr("stroke", "transparent")
        .attr("stroke-width", 1)
        .lower();

      // draw a full-width black baseline across the chart so it meets the y-axis
      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", innerHeight)
        .attr("y2", innerHeight)
        .style("stroke", "#000")
        .style("stroke-width", "3px")
        .style("stroke-linecap", "square");

      // draw vertical guideline at the Nationwide value (if available) — solid line, no label
      const nationwideValue = getPercentValue(nationwideItem);
      if (nationwideValue !== null && !isNaN(nationwideValue)) {
        const nx = x(Math.max(0, nationwideValue));
        // vertical solid line spanning the bar area
        g.append("line")
          .attr("x1", nx)
          .attr("x2", nx)
          .attr("y1", firstBarY)
          .attr("y2", innerHeight)
          .attr("stroke", "#666")
          .attr("stroke-width", 1);
      }

      // y-axis line at x=0 (left frame, starts below top axis labels to not overlay 0)
      g.append("line")
        .attr("x1", 0)
        .attr("x2", 0)
        .attr("y1", firstBarY)
        .attr("y2", innerHeight)
        .style("stroke-linecap", "square");

      // (nationwide detection helper removed; rendering handled separately above)

      // y labels: render Nationwide first, then states using a band scale for states
      // Nationwide label
      if (nationwideItem) {
        g.append("text")
          .attr("x", -8)
          .attr("y", firstBarY + nationwideHeight / 2)
          .attr("dy", "0.35em")
          .attr("text-anchor", "end")
          .text("Nationwide")
          .style("font-size", "1.05rem")
          .style("font-weight", "bold");
      }
      // states band scale
      const yStates = d3.scaleBand().domain(stateItems.map((d, i) => i)).range([nationwideItem ? (firstBarY + nationwideHeight) : firstBarY, innerHeight]).padding(0.6);
      // state labels
      g.append("g")
        .selectAll("text.stateLabel")
        .data(stateItems)
        .join("text")
        .attr("class", "stateLabel")
        .attr("x", -8)
        .attr("y", (d, i) => yStates(i) + yStates.bandwidth() / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", "end")
        .text((d) => (d.LocationTxt || d.LocationDesc || d.Location))
        .style("font-size", "1.05rem")
        .style("fill", "#000");
      // bars: Nationwide (separate), then states
      if (nationwideItem) {
        g.append("rect")
          .attr("x", 0)
          .attr("y", firstBarY)
          .attr("height", nationwideHeight)
          .attr("width", x(Math.max(0, getPercentValue(nationwideItem) ?? 0)))
          .attr("fill", allBarColor);
      }
      // state bars
      g.selectAll("rect.state").data(stateItems).join("rect")
        .attr("class", "state")
        .attr("x", 0)
        .attr("y", (d, i) => yStates(i))
        .attr("height", yStates.bandwidth())
        .attr("width", (d) => {
          const val = getPercentValue(d);
          // Ensure 0 is treated as a valid value (not null/undefined)
          return val !== null && val !== undefined ? x(Math.max(0, val)) : 0;
        })
        .attr("fill", allBarColor);

      // numeric percent labels: Nationwide then states
      if (nationwideItem && allMode !== "ci") {
        const v = getPercentValue(nationwideItem);
        const barRightX = x(Math.max(0, v ?? 0));
        const yy = firstBarY + nationwideHeight / 2;
        const txt = g.append("text")
          .attr("x", barRightX + 6)
          .attr("y", yy)
          .attr("dominant-baseline", "middle")
          .attr("alignment-baseline", "middle")
          .attr("dy", "0.1em")
          .attr("text-anchor", "start")
          .text(formatPercentLabel(nationwideItem))
          .style("font-size", "1.05rem")
          .style("fill", "#000");
        try {
          const bbox = txt.node() && txt.node().getBBox ? txt.node().getBBox() : { width: 0 };
          const margin = 6;
          // If label would overflow chart, place inside bar with white text
          if ((barRightX + margin + bbox.width) > innerWidth || (v !== null && v >= HIGH_VALUE_THRESHOLD)) {
            txt.attr("x", Math.max(0, barRightX - margin)).attr("text-anchor", "end").style("fill", "#fff");
          }
          // Only resolve CI overlap when actually displaying CIs
          if (allMode === "ci" && v !== null) {
            const ci = computeCIRange(nationwideItem);
            const lx = ci ? x(Math.max(0, ci.lower)) : null;
            const ux = ci ? x(Math.max(0, ci.upper)) : null;
            resolveCIOverlap(txt, bbox, lx, ux, barRightX, innerWidth, margin);
          }
        } catch (e) { /* ignore measurement failures */ }
      }
      if (allMode !== "ci") {
        const labels = g.selectAll("text.statePercent").data(stateItems).join("text")
          .attr("class", "statePercent")
          .attr("x", (d) => x(Math.max(0, getPercentValue(d) ?? 0)) + 6)
          .attr("y", (d, i) => yStates(i) + yStates.bandwidth() / 2)
          .attr("dominant-baseline", "middle")
          .attr("alignment-baseline", "middle")
          .attr("dy", "0.1em")
          .attr("text-anchor", "start")
          .text((d) => formatPercentLabel(d))
          .style("font-size", "1.05rem")
          .style("fill", "#000");
        // Post-process each label to move inside when near 100 and avoid CI overlap
        labels.each(function(d, i) {
          try {
            const txt = d3.select(this);
            const v = getPercentValue(d);
            const barRightX = x(Math.max(0, v ?? 0));
            const bbox = txt.node() && txt.node().getBBox ? txt.node().getBBox() : { width: 0 };
            const margin = 6;
            if ((barRightX + margin + bbox.width) > innerWidth || (v !== null && v >= HIGH_VALUE_THRESHOLD)) {
              txt.attr("x", Math.max(0, barRightX - margin)).attr("text-anchor", "end").style("fill", "#fff");
            }
            // Only resolve CI overlap when actually displaying CIs
            if (allMode === "ci" && v !== null) {
              const ci = computeCIRange(d);
              const lx = ci ? x(Math.max(0, ci.lower)) : null;
              const ux = ci ? x(Math.max(0, ci.upper)) : null;
              resolveCIOverlap(txt, bbox, lx, ux, barRightX, innerWidth, margin);
            }
          } catch (e) { /* ignore per-label issues */ }
        });
      }

      // vertical separator line at the left edge of the chart area (next to y-axis labels)
      const separator = g.append("line")
        .attr("x1", 0)
        .attr("x2", 0)
        // start the separator at the top axis Y so it doesn't overlap the top x-axis labels
        .attr("y1", Math.max(0, axisY + 2))
        .attr("y2", innerHeight)
        .style("stroke", "#000")
        .style("stroke-width", "3px")
        .style("stroke-linecap", "square");
      // bring separator to front so it's visible over bars/text
      if (separator.raise) separator.raise();

      // If CI mode requested, try to draw error bars when available
      if (allMode === "ci") {
        // Helper to determine vertical position for an item (nationwide + states)
        const getCIY = (d, i) => {
          if (nationwideItem) {
            if (i === 0) return firstBarY + nationwideHeight / 2;
            const stateIndex = i - 1;
            return yStates(stateIndex) + yStates.bandwidth() / 2;
          }
          return yStates(i) + yStates.bandwidth() / 2;
        };

        items.forEach((d, i) => {
          const ci = computeCIRange(d);
          if (!ci) {
            const yy = getCIY(d, i);
            const barRightX = x(Math.max(0, getPercentValue(d) ?? 0));
            if (isCensusSite(d)) {
              const txtNA = g.append("text")
                .attr("x", Math.min(innerWidth - 6, barRightX + 8))
                .attr("y", yy)
                .attr("dy", "0.35em")
                .attr("text-anchor", "start")
                .text("N/A")
                .style("font-size", "1.05rem")
                .style("fill", "#000");
              try {
                const bboxNA = txtNA.node() && txtNA.node().getBBox ? txtNA.node().getBBox() : { width: 0 };
                if ((barRightX + 8 + bboxNA.width) > innerWidth) {
                  txtNA.attr("x", barRightX - 8).attr("text-anchor", "end").style("fill", "#fff");
                }
              } catch (_) {}
            } else if (shouldShowDashForMissingCI(d) || shouldShowCIDash(d)) {
              const txtDash = g.append("text")
                .attr("x", Math.min(innerWidth - 6, barRightX + 8))
                .attr("y", yy)
                .attr("dy", "0.35em")
                .attr("text-anchor", "start")
                .text("—")
                .style("font-size", "1.05rem")
                .style("fill", "#000");
              try {
                const bboxDash = txtDash.node() && txtDash.node().getBBox ? txtDash.node().getBBox() : { width: 0 };
                if ((barRightX + 8 + bboxDash.width) > innerWidth) {
                  txtDash.attr("x", barRightX - 8).attr("text-anchor", "end").style("fill", "#fff");
                }
              } catch (_) {}
            }
            return;
          }
          const lx = x(Math.max(0, Math.min(100, ci.lower)));
          const ux = x(Math.max(0, Math.min(100, ci.upper)));
          const yy = getCIY(d, i);
          const barRightX = x(Math.max(0, getPercentValue(d) ?? 0));
          const insideColor = "#fff";
          // Match outside CI segments to the bar color
          const outsideColor = allBarColor;

          // Draw CI with color dependent on overlap with bar
          if (ux <= barRightX) {
            // Entirely inside bar
            g.append("line").attr("x1", lx).attr("x2", ux).attr("y1", yy).attr("y2", yy).attr("stroke", insideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", lx).attr("x2", lx).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", insideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", ux).attr("x2", ux).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", insideColor).attr("stroke-width", 2);
          } else if (lx >= barRightX) {
            // Entirely outside bar
            g.append("line").attr("x1", lx).attr("x2", ux).attr("y1", yy).attr("y2", yy).attr("stroke", outsideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", lx).attr("x2", lx).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", outsideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", ux).attr("x2", ux).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", outsideColor).attr("stroke-width", 2);
          } else {
            // Spans inside and outside: split at bar edge
            g.append("line").attr("x1", lx).attr("x2", barRightX).attr("y1", yy).attr("y2", yy).attr("stroke", insideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", barRightX).attr("x2", ux).attr("y1", yy).attr("y2", yy).attr("stroke", outsideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", lx).attr("x2", lx).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", insideColor).attr("stroke-width", 2);
            g.append("line").attr("x1", ux).attr("x2", ux).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", outsideColor).attr("stroke-width", 2);
          }
          // numeric range label to the right of upper cap
          const dashLabel = shouldShowCIDash(d) ? "—" : `${formatPercent(ci.lower)} - ${formatPercent(ci.upper)}`;
          // Place label to the right of the upper cap by default, vertically centered.
          // Ensure it appears to the right of the filled bar as well so it doesn't overlay the bar.
          const marginLabel = 6;
          const preferredX = Math.max(ux + 8, barRightX + 8);
          const txt = g.append("text")
            .attr("x", preferredX)
            .attr("y", yy)
            .attr("dy", "0.35em")
            .attr("text-anchor", "start")
            .text(dashLabel)
            .style("font-size", "1.05rem")
            .style("fill", "#000");
          // measure and flip to left side if it would overflow the chart area
          try {
            const bbox = txt.node() && txt.node().getBBox ? txt.node().getBBox() : { width: 0 };
            if ((preferredX + bbox.width + marginLabel) > innerWidth) {
              // move to left of lower cap and right-align text
              const altX = lx - 8;
              txt.attr("x", altX).attr("text-anchor", "end");
            }
            // After final position, if the label sits entirely inside the bar,
            // switch to white for contrast against the filled bar color.
            const bbox2 = txt.node() && txt.node().getBBox ? txt.node().getBBox() : { width: 0 };
            const xNow = Number(txt.attr("x"));
            const anchor = txt.attr("text-anchor") || "start";
            const rightEdge = anchor === "end" ? xNow : (xNow + bbox2.width);
            if (rightEdge <= barRightX) {
              txt.style("fill", "#fff");
            }
          } catch (e) {
            // ignore measurement failures
          }
        });
      }

      // set viewBox and explicit css height so the state svg expands to fit all items
      svg.attr("viewBox", `0 0 ${width} ${svgHeight}`);
      svg.style("height", `${svgHeight}px`);
      svg.style("overflow", "visible");

      // --- Local Surveys chart (render into second SVG) ---
      const localItems = source
        .filter((s) => (s.LocationType || "").toLowerCase() === "local")
        .filter((s) => !isSuppressedValue(s))
        // Hide locations with missing or exactly 0% values; always allow 100%
        .filter((s) => {
          const v = getPercentValue(s);
          return v !== null && v !== 0;
        })
        // When in CI mode, exclude locations without valid CI data (N/A)
        .filter((s) => {
          if (allMode === "ci") {
            return computeCIRange(s) !== null;
          }
          return true;
        })
        .slice()
        .sort((a, b) => ((a.LocationTxt || a.LocationDesc || "").localeCompare(b.LocationTxt || b.LocationDesc || "")));

      if (localItems && localItems.length > 0 && svgLocalRef && svgLocalRef.current) {
        const svg2 = d3.select(svgLocalRef.current);
        const width2 = Math.max(300, (svgLocalRef.current && svgLocalRef.current.clientWidth) || 600);
        const margin2 = { top: 28, right: 20, bottom: 40, left: 220 };
        const innerWidth2 = width2 - margin2.left - margin2.right;
        const perRowHeight2 = perRowHeight;
        const innerHeight2 = Math.max(120, localItems.length * perRowHeight2);
        const svgHeight2 = innerHeight2 + margin2.top + margin2.bottom + 20;

        const g2 = svg2.append("g").attr("transform", `translate(${margin2.left},${margin2.top})`);
        const x2 = d3.scaleLinear().domain([0, 100]).range([0, innerWidth2]);
        const xAxisTop2 = d3.axisTop(x2).tickValues([0, 25, 50, 75, 100]).tickFormat((d) => `${d}`).tickSize(0);
        const axisY2 = (localItems && localItems.length > 0) ? (innerHeight2 * 0) : 0;
        const firstBarY2 = axisY2 + 4; // start first bar 4px below axis line (match State)
        const y2 = d3.scaleBand().domain(localItems.map((d, i) => i)).range([firstBarY2, innerHeight2]).padding(0.6);
        const topAxisG2 = g2.append("g").attr("transform", `translate(0, ${axisY2})`).call(xAxisTop2);
        topAxisG2.selectAll("text").style("font-size", "14px").attr("dy", "-0.5em");
        topAxisG2.selectAll("path").style("stroke", "#000").style("stroke-width", "2px").style("stroke-linecap", "square");
        topAxisG2.selectAll("line").remove();
        const bottomAxisG2 = g2.append("g").attr("transform", `translate(0, ${innerHeight2})`).call(d3.axisBottom(x2).tickValues([0, 25, 50, 75, 100]).tickFormat((d) => `${d}`).tickSize(0));
        bottomAxisG2.selectAll("text").style("font-size", "14px").attr("dy", "1.2em");
        bottomAxisG2.selectAll("path").style("stroke", "#000").style("stroke-width", "2px").style("stroke-linecap", "square");
        bottomAxisG2.selectAll("line").remove();

        // y-axis line at x=0 (left frame, starts below top axis labels to not overlay 0)
        g2.append("line")
          .attr("x1", 0)
          .attr("x2", 0)
          .attr("y1", firstBarY2)
          .attr("y2", innerHeight2)
          .style("stroke-linecap", "square");

        // full-width baseline for local chart
        g2.append("line")
          .attr("x1", 0)
          .attr("x2", innerWidth2)
          .attr("y1", innerHeight2)
          .attr("y2", innerHeight2)
          .style("stroke", "#000")
          .style("stroke-width", "3px")
          .style("stroke-linecap", "square");

        // vertical grid lines for local chart at x tick positions
        const xTicks2 = [0, 25, 50, 75, 100];
        g2.selectAll("line.vgrid2").data(xTicks2).join("line")
          .attr("class", "vgrid2")
          .attr("x1", (d) => x2(d))
          .attr("x2", (d) => x2(d))
          .attr("y1", firstBarY2)
          .attr("y2", innerHeight2)
          .attr("stroke", "transparent")
          .attr("stroke-width", 1)
          .lower();

        // y labels for local items
        g2.append("g")
          .selectAll("text")
          .data(localItems)
          .join("text")
          .attr("x", -8)
          .attr("y", (d, i) => y2(i) + y2.bandwidth() / 2)
          .attr("dy", "0.35em")
          .attr("text-anchor", "end")
          .text((d) => (d.LocationTxt || d.LocationDesc || d.Location))
          .style("font-size", "1.05rem");

        // bars for local items
        g2.selectAll("rect")
          .data(localItems)
          .join("rect")
          .attr("x", 0)
          .attr("y", (d, i) => y2(i))
          .attr("height", y2.bandwidth())
          .attr("width", (d) => Math.max(0, x2(Math.max(0, getPercentValue(d) ?? 0))))
          .attr("fill", (d) => (isAll ? allBarColor : localColor));

        // numeric percent labels for local (hide when CI mode active)
        if (allMode !== "ci") {
          const lbls2 = g2.selectAll("percent-label-local")
            .data(localItems)
            .join("text")
            .attr("x", (d) => x2(Math.max(0, getPercentValue(d) ?? 0)) + 6)
            .attr("y", (d, i) => y2(i) + y2.bandwidth() / 2)
            .attr("dominant-baseline", "middle")
            .attr("alignment-baseline", "middle")
            .attr("dy", "0.1em")
            .attr("text-anchor", "start")
            .text((d) => formatPercentLabel(d))
            .style("font-size", "1.05rem")
            .style("fill", "#000");
          // Adjust position if near 100 or overlapping CI
          lbls2.each(function(d, i) {
            try {
              const txt = d3.select(this);
              const v = getPercentValue(d);
              const barRightX2 = x2(Math.max(0, v ?? 0));
              const bbox2 = txt.node() && txt.node().getBBox ? txt.node().getBBox() : { width: 0 };
              const margin2 = 6;
              if ((barRightX2 + margin2 + bbox2.width) > innerWidth2 || (v !== null && v >= HIGH_VALUE_THRESHOLD)) {
                txt.attr("x", barRightX2 - margin2).attr("text-anchor", "end").style("fill", "#fff");
              }
              // Only resolve CI overlap when actually displaying CIs
              if (allMode === "ci" && v !== null) {
                const ci = computeCIRange(d);
                const lx2 = ci ? x2(Math.max(0, ci.lower)) : null;
                const ux2 = ci ? x2(Math.max(0, ci.upper)) : null;
                resolveCIOverlap(txt, bbox2, lx2, ux2, barRightX2, innerWidth2, margin2);
              }
            } catch (e) { /* ignore */ }
          });
        }

        // If CI mode requested, draw 95% CI intervals for local items
        if (allMode === "ci") {
          localItems.forEach((d, i) => {
            const ci = computeCIRange(d);
            if (!ci) {
              const yy = y2(i) + y2.bandwidth() / 2;
              const barRightX2 = x2(Math.max(0, getPercentValue(d) ?? 0));
              if (isCensusSite(d)) {
                const txtNA2 = g2.append("text")
                  .attr("x", Math.min(innerWidth2 - 6, barRightX2 + 8))
                  .attr("y", yy)
                  .attr("dy", "0.35em")
                  .attr("text-anchor", "start")
                  .text("N/A")
                  .style("font-size", "1.05rem")
                  .style("fill", "#000");
                try {
                  const bboxNA2 = txtNA2.node() && txtNA2.node().getBBox ? txtNA2.node().getBBox() : { width: 0 };
                  if ((barRightX2 + 8 + bboxNA2.width) > innerWidth2) {
                    txtNA2.attr("x", barRightX2 - 8).attr("text-anchor", "end").style("fill", "#fff");
                  }
                } catch (_) {}
              } else if (shouldShowDashForMissingCI(d) || shouldShowCIDash(d)) {
                const txtDash2 = g2.append("text")
                  .attr("x", Math.min(innerWidth2 - 6, barRightX2 + 8))
                  .attr("y", yy)
                  .attr("dy", "0.35em")
                  .attr("text-anchor", "start")
                  .text("—")
                  .style("font-size", "1.05rem")
                  .style("fill", "#000");
                try {
                  const bboxDash2 = txtDash2.node() && txtDash2.node().getBBox ? txtDash2.node().getBBox() : { width: 0 };
                  if ((barRightX2 + 8 + bboxDash2.width) > innerWidth2) {
                    txtDash2.attr("x", barRightX2 - 8).attr("text-anchor", "end").style("fill", "#fff");
                  }
                } catch (_) {}
              }
              return;
            }
            const lx = x2(Math.max(0, Math.min(100, ci.lower)));
            const ux = x2(Math.max(0, Math.min(100, ci.upper)));
            const yy = y2(i) + y2.bandwidth() / 2;
            const barRightX2 = x2(Math.max(0, getPercentValue(d) ?? 0));
            const insideColor2 = "#fff";
            // Match outside CI segments to the bar color for local chart
            const outsideColor2 = allBarColor;
            if (ux <= barRightX2) {
              g2.append("line").attr("x1", lx).attr("x2", ux).attr("y1", yy).attr("y2", yy).attr("stroke", insideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", lx).attr("x2", lx).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", insideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", ux).attr("x2", ux).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", insideColor2).attr("stroke-width", 2);
            } else if (lx >= barRightX2) {
              g2.append("line").attr("x1", lx).attr("x2", ux).attr("y1", yy).attr("y2", yy).attr("stroke", outsideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", lx).attr("x2", lx).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", outsideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", ux).attr("x2", ux).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", outsideColor2).attr("stroke-width", 2);
            } else {
              g2.append("line").attr("x1", lx).attr("x2", barRightX2).attr("y1", yy).attr("y2", yy).attr("stroke", insideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", barRightX2).attr("x2", ux).attr("y1", yy).attr("y2", yy).attr("stroke", outsideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", lx).attr("x2", lx).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", insideColor2).attr("stroke-width", 2);
              g2.append("line").attr("x1", ux).attr("x2", ux).attr("y1", yy - 6).attr("y2", yy + 6).attr("stroke", outsideColor2).attr("stroke-width", 2);
            }
            const dashLabel2 = shouldShowCIDash(d) ? "—" : `${formatPercent(ci.lower)} - ${formatPercent(ci.upper)}`;
            const marginLabel2 = 6;
            const preferredX2 = Math.max(ux + 8, barRightX2 + 8);
            const txt2 = g2.append("text")
              .attr("x", preferredX2)
              .attr("y", yy)
              .attr("dy", "0.35em")
              .attr("text-anchor", "start")
              .text(dashLabel2)
              .style("font-size", "1.05rem")
              .style("fill", "#000");
            try {
              const bbox2 = txt2.node() && txt2.node().getBBox ? txt2.node().getBBox() : { width: 0 };
              if ((preferredX2 + bbox2.width + marginLabel2) > innerWidth2) {
                txt2.attr("x", lx - 8).attr("text-anchor", "end");
              }
              // If CI label is within the filled bar, make it white for contrast
              const bbox3 = txt2.node() && txt2.node().getBBox ? txt2.node().getBBox() : { width: 0 };
              const xNow2 = Number(txt2.attr("x"));
              const anchor2 = txt2.attr("text-anchor") || "start";
              const right2 = anchor2 === "end" ? xNow2 : (xNow2 + bbox3.width);
              if (right2 <= barRightX2) {
                txt2.style("fill", "#fff");
              }
            } catch (e) { /* ignore */ }
          });
        }

        // draw vertical guideline at the Nationwide value for local chart (if available)
        const nationwideValueLocal = nationwideValue;
        if (nationwideValueLocal !== null && !isNaN(nationwideValueLocal)) {
          const nx2 = x2(Math.max(0, nationwideValueLocal));
          g2.append("line")
            .attr("x1", nx2)
            .attr("x2", nx2)
            .attr("y1", 0)
            .attr("y2", innerHeight2)
            .attr("stroke", "#666")
            .attr("stroke-width", 1);
        }

        // left separator for local chart
        const sep2 = g2.append("line")
          .attr("x1", 0)
          .attr("x2", 0)
          .attr("y1", Math.max(0, axisY2 + 2))
          .attr("y2", innerHeight2)
          .style("stroke", "#000")
          .style("stroke-width", "3px")
          .style("stroke-linecap", "square");
        if (sep2.raise) sep2.raise();

        svg2.attr("viewBox", `0 0 ${width2} ${svgHeight2}`);
        svg2.style("height", `${svgHeight2}px`);
        svg2.style("overflow", "visible");
      }

      // All/Local charts rendered above — stop further rendering for All view when in All mode
      return;
    }

    // If we fetched multiple subquestion series into patternsMap, render one chart per subquestion
    if (question && question.SubQuestions && question.SubQuestions.length > 0 && !subQuestion && patternsMap && Object.keys(patternsMap).length > 0) {
      // iterate in question.SubQuestions order so charts follow the subquestion order
      question.SubQuestions.forEach((sq, idx) => {
        const dataObj = patternsMap[sq.SubQuestionId];
        const pd = Array.isArray(dataObj) ? dataObj : dataObj && dataObj.tableData ? dataObj.tableData : [];
        if (!pd || pd.length === 0) return;

        const svgEl = svgPatternRefs.current && svgPatternRefs.current[idx] ? d3.select(svgPatternRefs.current[idx]) : null;
        if (!svgEl) return;

        svgEl.selectAll("*").remove();
        const widthEl = Math.max(300, (svgPatternRefs.current[idx] && svgPatternRefs.current[idx].clientWidth) || 600);
        const margin = { top: 36, right: 90, bottom: 40, left: 48 };
        const innerWidth = widthEl - margin.left - margin.right;
        const svgHeightLocal = 320;
        const innerHeight = svgHeightLocal - margin.top - margin.bottom;
        svgEl.attr("viewBox", `0 0 ${widthEl} ${svgHeightLocal}`);
        svgEl.style("height", `${svgHeightLocal}px`).style("overflow", "visible");

        const g = svgEl.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

        const series = pd.slice().sort((a, b) => (a.Yr || a.Year) - (b.Yr || b.Year));
        const yearsAll = series.map((d) => d.Yr || d.Year);
        const uniqueYears = Array.from(new Set(yearsAll.map((y) => (y === undefined || y === null ? y : String(y))))).filter((y) => y !== undefined && y !== null).map((y) => (isNaN(Number(y)) ? y : Number(y)));

        const xPadding = Math.min(60, Math.round(innerWidth * 0.08));
        const x = d3.scalePoint().domain(uniqueYears).range([xPadding, innerWidth - xPadding]);
        
        // Calculate dynamic y-axis range when comparison is active
        let yMin = 0;
        let yMax = 100;
        let yTickValues = [0, 20, 40, 60, 80, 100];
        
        // For subquestions, check if compareData exists and calculate dynamic range
        let compareSeriesSQ = null;
        
        // First, check if we have per-subquestion compare data (from compareDataBySubQuestion)
        if (compareDataBySubQuestion && compareDataBySubQuestion[sq.SubQuestionId]) {
          const sqCompareData = compareDataBySubQuestion[sq.SubQuestionId];
          const raw = Array.isArray(sqCompareData.data) ? sqCompareData.data : (sqCompareData.data && sqCompareData.data.tableData ? sqCompareData.data.tableData : []);
          if (raw && raw.length > 0) {
            compareSeriesSQ = raw;
          }
        }
        
        // Fallback to compareData (for State/Local single-subquestion mode)
        if (!compareSeriesSQ && compareData && compareData.data) {
          const raw = Array.isArray(compareData.data) ? compareData.data : (compareData.data.tableData ? compareData.data.tableData : []);
          if (raw && raw.length > 0) {
            const targetText = normalizeText(sq.SubQuestion || "");
            const rowsByText = targetText
              ? raw.filter((r) => {
                  const rowText = r.SubQuestion || r.SubQuestionText || r.subQuestion || "";
                  return normalizeText(rowText) === targetText;
                })
              : [];
            const rowsForSQ = raw.filter((r) => r.SubQuestionId && String(r.SubQuestionId) === String(sq.SubQuestionId));

            if (rowsByText && rowsByText.length > 0) {
              compareSeriesSQ = deduplicateByYear(rowsByText, sq.SubQuestionId, sq.SubQuestion);
            } else if (rowsForSQ && rowsForSQ.length > 0) {
              compareSeriesSQ = deduplicateByYear(rowsForSQ, sq.SubQuestionId, sq.SubQuestion);
            }

            if (!compareSeriesSQ) {
              // If the compare fetch returned a single-subquestion series (no SubQuestionId on rows), use it
              if (!raw[0].SubQuestionId) compareSeriesSQ = raw;
            }
          }
        }
        
        if (compareMode && compareMode !== "None" && compareSeriesSQ && compareSeriesSQ.length > 0) {
          // Calculate min/max from both series
          const allValues = [];
          series.forEach((d) => {
            const v = getPercentValue(d);
            if (v !== null && !isNaN(v)) allValues.push(v);
          });
          
          compareSeriesSQ.forEach((d) => {
            const v = getPercentValue(d);
            if (v !== null && !isNaN(v)) allValues.push(v);
          });
          
          if (allValues.length > 0) {
            yMin = Math.min(...allValues);
            yMax = Math.max(...allValues);
            
            // Add padding (10% of range) and round to nearest 5
            const range = yMax - yMin;
            const padding = Math.max(5, Math.round(range * 0.1 / 5) * 5);
            yMin = Math.max(0, Math.round((yMin - padding) / 5) * 5);
            yMax = Math.min(100, Math.round((yMax + padding) / 5) * 5);
            
            // Generate appropriate tick values based on the range
            const tickInterval = yMax - yMin > 50 ? 20 : (yMax - yMin > 20 ? 10 : 5);
            yTickValues = [];
            for (let i = Math.ceil(yMin / tickInterval) * tickInterval; i <= yMax; i += tickInterval) {
              if (i >= yMin && i <= yMax) yTickValues.push(i);
            }
            // Ensure we have at least 2 ticks
            if (yTickValues.length === 0) {
              yTickValues = [yMin, yMax];
            }
          }
          // Do not fill missing Nationwide years; we now fetch per-year via GetComparison per subquestion
        }
        
        const y = d3.scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]);
        // Always show year labels on the x-axis
        const xAxis = d3.axisBottom(x).tickFormat((d) => d).tickSize(0);
        const yAxis = d3.axisLeft(y).tickValues(yTickValues).tickSize(0);
        // append y-axis; x-axis will be appended depending on chart type to avoid duplicates
        const yAxisG = g.append("g").call(yAxis);
        yAxisG.selectAll("text").style("font-size", "14px");
        // make y-axis path thicker and black to match x-axis; actual tick lines are disabled
        yAxisG.selectAll("path").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");

        // Title is rendered outside the SVG (in React JSX) so it can wrap naturally.
        // (Previously an SVG text element was appended here; removed to enable wrapping.)

        const line = d3.line()
          .x((d) => x(d.Yr || d.Year))
          .y((d) => y(getPercentValue(d)))
          .defined((d) => getPercentValue(d) !== null);

        if (uniqueYears.length >= 3) {
          g.append("path").datum(series).attr("fill", "none").attr("stroke", localColor).attr("stroke-width", 3).attr("d", line);
          // main-series points: solid circles with black fill and colored stroke
          const symMain = d3.symbol().type(d3.symbolCircle).size(63);
          g.selectAll("path.pointMain").data(series.filter((d) => getPercentValue(d) !== null)).join("path")
            .attr("class", "pointMain")
            .attr("d", symMain)
            .attr("transform", (d) => `translate(${x(d.Yr || d.Year)},${y(getPercentValue(d) ?? 0)})`)
            .attr("fill", "#000")
            .attr("stroke", localColor)
            .attr("stroke-width", 1.5);
          // numeric labels above each point (one decimal); stack higher when compare is close
          const compareByYearSQ = (compareSeriesSQ || []).reduce((acc, r) => {
            const yv = r.Yr || r.Year;
            if (yv !== undefined) acc[String(yv)] = r;
            return acc;
          }, {});
          g.selectAll("text.pointLabel").data(series.filter((d) => getPercentValue(d) !== null)).join("text")
            .attr("class", "pointLabel")
            .attr("x", (d) => x(d.Yr || d.Year))
            .attr("y", (d) => {
              const v = getPercentValue(d);
              if (v == null) return y(0) - 16;
              const yr = d.Yr || d.Year;
              const compRow = compareByYearSQ[String(yr)];
              const compVal = compRow ? getPercentValue(compRow) : null;
              if (compVal != null && Math.abs(compVal - v) < 3) {
                const topVal = Math.max(v, compVal);
                const topY = y(topVal);
                const isMainTop = v >= compVal;
                return isMainTop ? topY - 28 : topY - 12;
              }
              return y(v) - 16;
            })
            .attr("text-anchor", "middle")
            .text((d) => {
              return formatPercentLabel(d);
            })
            .style("font-size", "0.95rem")
            .style("fill", "#000");
          // append x-axis for line charts and hide edge labels
          const xAxisG_line = g.append("g").attr("transform", `translate(0, ${innerHeight})`).call(xAxis);
          xAxisG_line.selectAll("text").style("font-size", "14px").attr("dy", "1.2em");
          xAxisG_line.selectAll("path").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");
          xAxisG_line.selectAll("line").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");
          // full-width baseline (ensure axis meets y-axis even if tick range starts later)
          g.append("line")
            .attr("x1", 0)
            .attr("x2", innerWidth)
            .attr("y1", innerHeight)
            .attr("y2", innerHeight)
            .style("stroke", "#000")
            .style("stroke-width", "3px")
            .style("stroke-linecap", "square");
          // show all year labels on the x-axis for line charts

          // compare overlay for this subquestion (line)
            if (compareMode && compareMode !== "None" && compareSeriesSQ && compareSeriesSQ.length > 0) {
            const comp = compareSeriesSQ.slice().sort((a, b) => (a.Yr || a.Year) - (b.Yr || b.Year)).filter((d) => getPercentValue(d) !== null);
            const line2 = d3.line()
              .x((d) => x(d.Yr || d.Year))
              .y((d) => y(getPercentValue(d)))
              .defined((d) => getPercentValue(d) !== null);
            // compare line: dashed gray (fewer, longer dashes)
            g.append("path").datum(comp).attr("fill", "none").attr("stroke", getCompareColor()).attr("stroke-width", 3).attr("stroke-dasharray", "40,10").attr("d", line2);
            // compare points: triangles with black fill and gray stroke
            const symComp = d3.symbol().type(d3.symbolTriangle).size(90);
            g.selectAll("path.pointCompSQ").data(comp).join("path")
              .attr("class", "pointCompSQ")
              .attr("d", symComp)
              .attr("transform", (d) => `translate(${x(d.Yr || d.Year)},${y(getPercentValue(d) ?? 0)})`)
              .attr("fill", "#000")
              .attr("stroke", getCompareColor())
              .attr("stroke-width", 1.5);
            g.selectAll("text.pointLabelCompSQ").data(comp).join("text")
              .attr("class", "pointLabelCompSQ")
              .attr("x", (d) => x(d.Yr || d.Year))
              .attr("y", (d) => {
                const v = getPercentValue(d);
                if (v == null) return y(0) - 16;
                const yr = d.Yr || d.Year;
                const mainRow = series.find((s) => (s.Yr || s.Year) === yr);
                const mainVal = mainRow ? getPercentValue(mainRow) : null;
                if (mainVal != null && Math.abs(mainVal - v) < 3) {
                  const topVal = Math.max(v, mainVal);
                  const topY = y(topVal);
                  const isCompTop = v >= mainVal;
                  return isCompTop ? topY - 28 : topY - 12;
                }
                return y(v) - 16;
              })
              .attr("text-anchor", "middle")
              .text((d) => formatPercentLabelAllowMissingCI(d))
              .style("font-size", "0.95rem")
              .style("fill", "#000");
          }
          // draw horizontal grid lines for the y-axis tick values (light gray)
          const gridTicks = yTickValues;
          g.selectAll("line.hgrid").data(gridTicks).join("line")
            .attr("class", "hgrid")
            .attr("x1", 0)
            .attr("x2", innerWidth)
            .attr("y1", (d) => y(d))
            .attr("y2", (d) => y(d))
            .attr("stroke", "#e6e6e6")
            .attr("stroke-width", 1)
            .lower();
        } else {
          // reduce padding so bars can be narrower and sit under ticks
          const band = d3.scaleBand().domain(uniqueYears).range([0, innerWidth]).padding(0.6);
          const bw = band.bandwidth();
          // For a specific location (state or local), use a fixed bar width
          const fixedBarWidth = 40; // px per user request
          // compute single-bar width: prefer fixed width when viewing a single location,
          // but fall back to scaled width if the band is too narrow
          const singleBarWidth = (isState || isLocal)
            ? (2 * fixedBarWidth + 8 <= bw ? fixedBarWidth : Math.max(6, Math.floor(bw * 0.36)))
            : Math.max(6, Math.floor(bw * 0.36));
          const barOffset = (bw - singleBarWidth) / 2;
          const xBandG = g.append("g").attr("transform", `translate(0, ${innerHeight})`).call(d3.axisBottom(band).tickFormat((d) => {
            if (!uniqueYears || uniqueYears.length === 0) return d;
            if (uniqueYears.length <= 2) return d;
            const first = uniqueYears[0];
            const last = uniqueYears[uniqueYears.length - 1];
            return (String(d) === String(first) || String(d) === String(last)) ? "" : d;
          }).tickSize(0));
          xBandG.selectAll("text").style("font-size", "14px");

          // If compare series exists, draw a grouped pair (base left, compare right)
          if (compareMode && compareMode !== "None" && compareSeriesSQ && compareSeriesSQ.length > 0) {
            const compByYear = (compareSeriesSQ || []).reduce((acc, r) => {
              const yv = r.Yr || r.Year;
              if (yv !== undefined) acc[String(yv)] = r;
              return acc;
            }, {});

            // Prefer fixed 40px per-bar width for specific-location views
            const fixedWidth = 40;
            const desiredGap = 8;
            let bwInner, gap, groupOffset;
            if ((isState || isLocal) && (2 * fixedWidth + desiredGap <= bw)) {
              bwInner = fixedWidth;
              gap = desiredGap;
              const groupTotal = bwInner * 2 + gap;
              groupOffset = Math.max(0, Math.floor((bw - groupTotal) / 2));
            } else {
              // stronger gap + narrower inner bars so pairs do not overlap on narrow charts
              const minBar = Math.max(4, Math.floor(bw * 0.12));
              const minGap = 8;
              const desiredGapAdaptive = Math.round(bw * 0.22);
              const maxAllowedGap = Math.max(minGap, bw - 2 * minBar);
              gap = Math.min(Math.max(minGap, desiredGapAdaptive), maxAllowedGap);
              bwInner = Math.max(minBar, Math.floor((bw - gap) / 2));
              // if pair doesn't fit, shrink bars to fit the min gap
              if (bwInner * 2 + gap > bw) {
                bwInner = Math.max(4, Math.floor((bw - minGap) / 2));
                gap = Math.max(minGap, bw - 2 * bwInner);
              }
              const groupTotal = bwInner * 2 + gap;
              groupOffset = Math.max(0, Math.floor((bw - groupTotal) / 2));
            }

            // base (left) bars
            g.selectAll("rect.baseSQ").data(series).join("rect")
              .attr("class", "baseSQ")
              .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset)
              .attr("y", (d) => y(getPercentValue(d) ?? 0))
              .attr("width", bwInner)
              .attr("height", (d) => innerHeight - y(getPercentValue(d) ?? 0))
              .attr("fill", (d) => (isAll ? allBarColor : localColor));

            // compare (right) bars
            g.selectAll("rect.compSQ").data(series).join("rect")
              .attr("class", "compSQ")
              .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset + bwInner + gap)
              .attr("y", (d) => {
                const c = compByYear[String(d.Yr || d.Year)];
                return y(getPercentValue(c) ?? 0);
              })
              .attr("width", bwInner)
              .attr("height", (d) => {
                const c = compByYear[String(d.Yr || d.Year)];
                return innerHeight - y(getPercentValue(c) ?? 0);
              })
              .attr("fill", getCompareColor());

            // numeric labels for base and compare bars
            g.selectAll("text.baseLabelSQ").data(series).join("text")
              .attr("class", "baseLabelSQ")
              .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset + bwInner / 2)
              .attr("y", (d) => y(getPercentValue(d) ?? 0) - 6)
              .attr("text-anchor", "middle")
              .text((d) => formatPercentLabel(d))
              .style("font-size", "0.95rem")
              .style("fill", "#000");

            g.selectAll("text.compLabelSQ").data(series).join("text")
              .attr("class", "compLabelSQ")
              .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset + bwInner + gap + bwInner / 2)
              .attr("y", (d) => {
                const c = compByYear[String(d.Yr || d.Year)];
                return y(getPercentValue(c) ?? 0) - 6;
              })
              .attr("text-anchor", "middle")
              .text((d) => {
                const c = compByYear[String(d.Yr || d.Year)];
                return formatPercentLabelAllowMissingCI(c);
              })
              .style("font-size", "0.95rem")
              .style("fill", "#000");
          } else {
            // single narrower bars centered in the band
            g.selectAll("rect").data(series).join("rect")
              .attr("x", (d) => (band(d.Yr || d.Year) || 0) + barOffset)
              .attr("y", (d) => y(getPercentValue(d) ?? 0))
              .attr("width", singleBarWidth)
              .attr("height", (d) => innerHeight - y(getPercentValue(d) ?? 0))
              .attr("fill", (d) => (isAll ? allBarColor : localColor));

            // numeric labels above each bar (per-subquestion)
            g.selectAll("text.sqBarLabel").data(series).join("text")
              .attr("class", "sqBarLabel")
              .attr("x", (d) => (band(d.Yr || d.Year) || 0) + barOffset + singleBarWidth / 2)
              .attr("y", (d) => {
                const v = getPercentValue(d);
                return v != null ? y(v) - 6 : y(0) - 6;
              })
              .attr("text-anchor", "middle")
              .text((d) => formatPercentLabel(d))
              .style("font-size", "0.95rem")
              .style("fill", "#000");
          }
        }
      });

      return;
    }

    // For State or Local: use patternsData
    const pd = normalizeRows(patternsData);
    if (!pd || pd.length === 0) {
      svg.append("text").attr("x", 20).attr("y", 40).text("No data available");
      return;
    }

    // patterns endpoint often returns rows keyed by Yr
    const series = pd.slice().sort((a, b) => (a.Yr || a.Year) - (b.Yr || b.Year));
    // derive years and the set of unique years — decide chart type by unique year count
    const yearsAll = series.map((d) => d.Yr || d.Year);
    const uniqueYears = Array.from(new Set(yearsAll.map((y) => (y === undefined || y === null ? y : String(y))))).filter((y) => y !== undefined && y !== null).map((y) => (isNaN(Number(y)) ? y : Number(y)));

    const margin = { top: 36, right: 90, bottom: 40, left: 48 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = svgHeight - margin.top - margin.bottom;
    // ensure viewBox/height are set for non-All views
    svg.attr("viewBox", `0 0 ${width} ${svgHeight}`);
    svg.style("height", `${svgHeight}px`);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const xPadding = Math.min(60, Math.round(innerWidth * 0.08));
    const x = d3.scalePoint().domain(uniqueYears).range([xPadding, innerWidth - xPadding]);
    
    // prepare compare series if available (needed before y-axis range calculation)
    let compareSeries = compareData && (Array.isArray(compareData.data) ? compareData.data : (compareData.data && compareData.data.tableData ? compareData.data.tableData : null));
    let compareByYear = (compareSeries || []).reduce((acc, r) => {
      const yv = r.Yr || r.Year;
      if (yv !== undefined) acc[String(yv)] = r;
      return acc;
    }, {});
    
    // Calculate dynamic y-axis range when comparison is active
    let yMin = 0;
    let yMax = 100;
    let yTickValues = [0, 20, 40, 60, 80, 100];
    
    if (compareMode && compareMode !== "None") {
      // When comparing, calculate min/max from both series
      const allValues = [];
      series.forEach((d) => {
        const v = getPercentValue(d);
        if (v !== null && !isNaN(v)) allValues.push(v);
      });
      
      if (compareSeries && compareSeries.length > 0) {
        compareSeries.forEach((d) => {
          const v = getPercentValue(d);
          if (v !== null && !isNaN(v)) allValues.push(v);
        });
      }
      
      if (allValues.length > 0) {
        yMin = Math.min(...allValues);
        yMax = Math.max(...allValues);
        
        // Add padding (10% of range) and round to nearest 5
        const range = yMax - yMin;
        const padding = Math.max(5, Math.round(range * 0.1 / 5) * 5);
        yMin = Math.max(0, Math.round((yMin - padding) / 5) * 5);
        yMax = Math.min(100, Math.round((yMax + padding) / 5) * 5);
        
        // Generate appropriate tick values based on the range
        const tickInterval = yMax - yMin > 50 ? 20 : (yMax - yMin > 20 ? 10 : 5);
        yTickValues = [];
        for (let i = Math.ceil(yMin / tickInterval) * tickInterval; i <= yMax; i += tickInterval) {
          if (i >= yMin && i <= yMax) yTickValues.push(i);
        }
        // Ensure we have at least 2 ticks
        if (yTickValues.length === 0) {
          yTickValues = [yMin, yMax];
        }
      }
      // Do not duplicate Nationwide values across years; rely on compare fetch
      // which already attempts per-year filling via GetComparison.
    }
    
    const y = d3.scaleLinear().domain([yMin, yMax]).range([innerHeight, 0]);

    // Always show year labels on the x-axis
    const xAxis = d3.axisBottom(x).tickFormat((d) => d).tickSize(0);
    const yAxis = d3.axisLeft(y).tickValues(yTickValues).tickSize(0);
    const yAxisG = g.append("g").call(yAxis);
    yAxisG.selectAll("text").style("font-size", "14px");
    // make y-axis path thicker and black to match x-axis; actual tick lines are disabled
    yAxisG.selectAll("path").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");

    // Y-axis label: Percentage (%)
    svg.append("text")
      .attr("transform", `translate(${margin.left - 40}, ${margin.top + innerHeight/2}) rotate(-90)`)
      .attr("text-anchor", "middle")
      .style("font-size", 16)
      .style("font-weight", 400)
      .text("Percentage (%)");

    // Title (question text) is rendered inside each chart SVG. Do not render
    // an external header above the charts (keeps chart titles consistent).

    const line = d3.line()
      .x((d) => x(d.Yr || d.Year))
      .y((d) => y(getPercentValue(d)))
      .defined((d) => getPercentValue(d) !== null);

    // Fallback: if comparing to Nationwide (abbr === 'ALL') and the compare
    // series is empty, try to derive a single-point nationwide series from
    // the All-Locations payload so local/state charts still show a compare
    // marker. Use the same year domain as the base `series` so the point
    // appears on the chart.
    if (compareMode && compareMode !== "None" && (!compareSeries || compareSeries.length === 0) && compareMode === "Nationwide" && allLocationsData) {
      try {
        const src = Array.isArray(allLocationsData) ? allLocationsData : (allLocationsData && allLocationsData.tableData ? allLocationsData.tableData : []);
        const nationwideItem = (src || []).find(
          (s) => (s.LocationAlias && String(s.LocationAlias).toLowerCase() === "national") || (s.LocationTxt && String(s.LocationTxt).toLowerCase() === "nationwide") || (s.LocationType && String(s.LocationType).toLowerCase() === "national")
        );
        if (nationwideItem) {
          const val = getPercentValue(nationwideItem);
          if (val !== null && !isNaN(val)) {
            // If the base series has multiple years, repeat the nationwide
            // value across those years so the compare line/points render.
            const visibleYears = (series && series.length > 0) ? series.map((s) => s.Yr || s.Year).filter((y) => y !== undefined && y !== null) : [];
            if (visibleYears && visibleYears.length > 0) {
              const fallbackSeries = visibleYears.map((yr) => ({ Yr: yr, Year: yr, NPercent: val, LocationTxt: nationwideItem.LocationTxt || "Nationwide", LocationAbbr: nationwideItem.LocationAbbr || "ALL" }));
              compareSeries = fallbackSeries;
              compareByYear = fallbackSeries.reduce((acc, r) => { acc[String(r.Yr || r.Year)] = r; return acc; }, {});
            } else {
              const baseYear = year || (nationwideItem.Yr || nationwideItem.Year);
              const fallbackRow = { Yr: baseYear, Year: baseYear, NPercent: val, LocationTxt: nationwideItem.LocationTxt || "Nationwide", LocationAbbr: nationwideItem.LocationAbbr || "ALL" };
              compareSeries = [fallbackRow];
              compareByYear = { [String(baseYear)]: fallbackRow };
            }
          }
        }
      } catch (e) {
        // ignore fallback failures — compare will remain empty
      }
    }

    if (uniqueYears.length >= 3) {
      // line chart - base (use local color)
      g.append("path").datum(series).attr("fill", "none").attr("stroke", localColor).attr("stroke-width", 3).attr("d", line);
      // base points as circles with black fill and teal stroke
      const symMainTop = d3.symbol().type(d3.symbolCircle).size(63);
      g.selectAll("path.pointMainTop").data(series.filter((d) => getPercentValue(d) !== null)).join("path")
        .attr("class", "pointMainTop")
        .attr("d", symMainTop)
        .attr("transform", (d) => `translate(${x(d.Yr || d.Year)},${y(getPercentValue(d) ?? 0)})`)
        .attr("fill", "#000")
        .attr("stroke", localColor)
        .attr("stroke-width", 1.5);
      // numeric labels above each main-series point; stack upward when close to compare series
          g.selectAll("text.pointLabelMain").data(series.filter((d) => getPercentValue(d) !== null)).join("text")
        .attr("class", "pointLabelMain")
        .attr("x", (d) => x(d.Yr || d.Year))
        .attr("y", (d) => {
          const v = getPercentValue(d);
          if (v == null) return y(0) - 16;
          const yr = d.Yr || d.Year;
          const compRow = compareByYear[String(yr)];
          const compVal = compRow ? getPercentValue(compRow) : null;
          if (compVal != null && Math.abs(compVal - v) < 3) {
            const topVal = Math.max(v, compVal);
            const topY = y(topVal);
            const isMainTop = v >= compVal;
            return isMainTop ? topY - 28 : topY - 12;
          }
          return y(v) - 16;
        })
        .attr("text-anchor", "middle")
        .text((d) => formatPercentLabel(d))
        .style("font-size", "1.05rem")
        .style("fill", "#000");
      // append x-axis for line charts and hide edge labels
      const xAxisG_main = g.append("g").attr("transform", `translate(0, ${innerHeight})`).call(xAxis);
      xAxisG_main.selectAll("text").style("font-size", "14px").attr("dy", "1.2em");
      xAxisG_main.selectAll("path").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");
      xAxisG_main.selectAll("line").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");
      // draw horizontal grid lines for the y-axis tick values (light gray)
      const gridTicksMain = yTickValues;
      g.selectAll("line.hgrid-main").data(gridTicksMain).join("line")
        .attr("class", "hgrid-main")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", (d) => y(d))
        .attr("y2", (d) => y(d))
        .attr("stroke", "#e6e6e6")
        .attr("stroke-width", 1)
        .lower();
      // ensure a full baseline is drawn from the left edge to the right edge of the chart
      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", innerHeight)
        .attr("y2", innerHeight)
        .style("stroke", "#000")
        .style("stroke-width", "3px")
        .style("stroke-linecap", "square");
      // show all year labels on the x-axis for line charts

      // compare overlay (line)
      if (compareMode && compareMode !== "None" && compareSeries && compareSeries.length > 0) {
            const comp = compareSeries.slice().sort((a, b) => (a.Yr || a.Year) - (b.Yr || b.Year)).filter((d) => getPercentValue(d) !== null);
        const line2 = d3.line()
          .x((d) => x(d.Yr || d.Year))
          .y((d) => y(getPercentValue(d)))
          .defined((d) => getPercentValue(d) !== null);
        // compare line: dashed gray (thicker, fewer, longer dashes)
        g.append("path").datum(comp).attr("fill", "none").attr("stroke", getCompareColor()).attr("stroke-width", 3).attr("stroke-dasharray", "80,40").attr("d", line2);
        // compare points as triangles with black fill and gray stroke
        const symCompMain = d3.symbol().type(d3.symbolTriangle).size(90);
        g.selectAll("path.pointLabelCompShape").data(comp).join("path")
          .attr("class", "pointLabelCompShape")
          .attr("d", symCompMain)
          .attr("transform", (d) => `translate(${x(d.Yr || d.Year)},${y(getPercentValue(d) ?? 0)})`)
          .attr("fill", "#000")
          .attr("stroke", getCompareColor())
          .attr("stroke-width", 1.5);
        // numeric labels for compare series points
        // When values are close to the main series, stack both labels above the top-most point
          g.selectAll("text.pointLabelComp").data(comp).join("text")
          .attr("class", "pointLabelComp")
          .attr("x", (d) => x(d.Yr || d.Year))
          .attr("y", (d) => {
            const v = getPercentValue(d);
            if (v == null) return y(0) - 16;
            const yr = d.Yr || d.Year;
            const mainRow = series.find((s) => (s.Yr || s.Year) === yr);
            const mainVal = mainRow ? getPercentValue(mainRow) : null;
            if (mainVal != null && Math.abs(mainVal - v) < 3) {
              const topVal = Math.max(v, mainVal);
              const topY = y(topVal);
              const isCompTop = v >= mainVal;
              return isCompTop ? topY - 28 : topY - 12;
            }
            return y(v) - 16;
          })
          .attr("text-anchor", "middle")
          .text((d) => formatPercentLabelAllowMissingCI(d))
          .style("font-size", "1.05rem")
          .style("fill", "#000");
      }
    } else {
      // bar chart across years (grouped if compare present)
      // Use a band scale so axis ticks line up under each bar and we can control bar width
      const band = d3.scaleBand().domain(uniqueYears).range([0, innerWidth]).padding(0.4);
      const bw = band.bandwidth();
      // For a specific location (state or local), prefer a fixed bar width
      const fixedBarWidth = 40; // px per user request
      const singleBarWidth = (isState || isLocal)
        ? (2 * fixedBarWidth + 8 <= bw ? fixedBarWidth : Math.max(6, Math.floor(bw * 0.36)))
        : Math.max(6, Math.floor(bw * 0.36));
      const barOffset = (bw - singleBarWidth) / 2;

      // render x-axis under the bars using the band scale so labels align
      const xAxisG_band = g.append("g")
        .attr("transform", `translate(0, ${innerHeight})`)
          .call(d3.axisBottom(band).tickFormat((d) => {
          if (!uniqueYears || uniqueYears.length === 0) return d;
          // For 1-2 years always show year labels; for longer series hide first/last
          if (uniqueYears.length <= 2) return d;
          const first = uniqueYears[0];
          const last = uniqueYears[uniqueYears.length - 1];
          return (String(d) === String(first) || String(d) === String(last)) ? "" : d;
          }).tickSize(0));
      xAxisG_band.selectAll("text").style("font-size", "14px").attr("dy", "1.2em");
      xAxisG_band.selectAll("path").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");
      xAxisG_band.selectAll("line").style("stroke", "#000").style("stroke-width", "3px").style("stroke-linecap", "square");
      // full-width baseline for bar charts (ensure meeting y-axis)
      g.append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", innerHeight)
        .attr("y2", innerHeight)
        .style("stroke", "#000")
        .style("stroke-width", "3px")
        .style("stroke-linecap", "square");

      if (compareMode && compareMode !== "None" && compareSeries && compareSeries.length > 0) {
        // Prefer fixed 40px per-bar width for specific-location views
        const fixedWidth = 40;
        const desiredGap = 8;
        let bwInner, gap, groupOffset;
        if ((isState || isLocal) && (2 * fixedWidth + desiredGap <= bw)) {
          bwInner = fixedWidth;
          gap = desiredGap;
          const groupTotal = bwInner * 2 + gap;
          groupOffset = Math.max(0, Math.floor((bw - groupTotal) / 2));
        } else {
          // grouped bars: base left, compare right — compute widths so pair fits inside the band
          const minBar = Math.max(4, Math.floor(bw * 0.12));
          const minGap = 8;
          const desiredGapAdaptive = Math.round(bw * 0.22);
          const maxAllowedGap = Math.max(minGap, bw - 2 * minBar);
          gap = Math.min(Math.max(minGap, desiredGapAdaptive), maxAllowedGap);
          bwInner = Math.max(minBar, Math.floor((bw - gap) / 2));
          if (bwInner * 2 + gap > bw) {
            bwInner = Math.max(4, Math.floor((bw - minGap) / 2));
            gap = Math.max(minGap, bw - 2 * bwInner);
          }
          const groupTotal = bwInner * 2 + gap;
          groupOffset = Math.max(0, Math.floor((bw - groupTotal) / 2));
        }
        

        // base (left) bars
        g.selectAll("rect.base").data(series).join("rect")
          .attr("class", "base")
          .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset)
          .attr("y", (d) => y(getPercentValue(d) ?? 0))
          .attr("width", bwInner)
          .attr("height", (d) => innerHeight - y(getPercentValue(d) ?? 0))
          .attr("fill", (d) => (isAll ? allBarColor : localColor));

        // compare (right) bars
        g.selectAll("rect.comp").data(series).join("rect")
          .attr("class", "comp")
          .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset + bwInner + gap)
          .attr("y", (d) => {
            const c = compareByYear[String(d.Yr || d.Year)];
            return y(getPercentValue(c) ?? 0);
          })
          .attr("width", bwInner)
          .attr("height", (d) => {
            const c = compareByYear[String(d.Yr || d.Year)];
            return innerHeight - y(getPercentValue(c) ?? 0);
          })
          .attr("fill", getCompareColor());

        // numeric labels above each grouped bar
        g.selectAll("text.baseLabel").data(series).join("text")
          .attr("class", "baseLabel")
          .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset + bwInner / 2)
          .attr("y", (d) => y(getPercentValue(d) ?? 0) - 6)
          .attr("text-anchor", "middle")
          .text((d) => formatPercentLabel(d))
          .style("font-size", "1.05rem")
          .style("fill", "#000");

        g.selectAll("text.compLabel").data(series).join("text")
          .attr("class", "compLabel")
          .attr("x", (d) => (band(d.Yr || d.Year) || 0) + groupOffset + bwInner + gap + bwInner / 2)
          .attr("y", (d) => {
            const c = compareByYear[String(d.Yr || d.Year)];
            return y(getPercentValue(c) ?? 0) - 6;
          })
          .attr("text-anchor", "middle")
          .text((d) => {
            const c = compareByYear[String(d.Yr || d.Year)];
            return formatPercentLabelAllowMissingCI(c);
          })
          .style("font-size", "1.05rem")
          .style("fill", "#000");
      } else {
        // single narrower bars centered in the band
        g.selectAll("rect").data(series).join("rect")
          .attr("x", (d) => (band(d.Yr || d.Year) || 0) + barOffset)
          .attr("y", (d) => y(getPercentValue(d) ?? 0))
          .attr("width", singleBarWidth)
          .attr("height", (d) => innerHeight - y(getPercentValue(d) ?? 0))
          .attr("fill", (d) => (isAll ? allBarColor : localColor));

        // numeric labels above each bar
        g.selectAll("text.barLabel").data(series).join("text")
          .attr("class", "barLabel")
          .attr("x", (d) => (band(d.Yr || d.Year) || 0) + barOffset + singleBarWidth / 2)
          .attr("y", (d) => y(getPercentValue(d) ?? 0) - 6)
          .attr("text-anchor", "middle")
          .text((d) => formatPercentLabel(d))
          .style("font-size", "1.05rem")
          .style("fill", "#000");
      }
    }

    // Legend removed per design — not rendering top-right legend
  }

  function parseConfidenceLower(ci) {
    if (!ci) return null;
    const m = String(ci).match(/([0-9]+\.?[0-9]*)[^0-9]+([0-9]+\.?[0-9]*)/);
    if (m) return m[1];
    return null;
  }
  function parseConfidenceUpper(ci) {
    if (!ci) return null;
    const m = String(ci).match(/([0-9]+\.?[0-9]*)[^0-9]+([0-9]+\.?[0-9]*)/);
    if (m) return m[2];
    return null;
  }

  // Dagger-based CI suppression no longer used
  function shouldSuppressCI() { return false; }

  // Try to derive a numeric sample-size from common field names present
  function parseSampleSize(row) {
    if (!row) return null;
    const fields = [
      'SampleSize', 'Sample_Size', 'N', 'n', 'NCount', 'UnweightedN', 'SampleSizeUnweighted', 'Denominator', 'Sample'
    ];
    for (let f of fields) {
      if (row[f] !== undefined && row[f] !== null) {
        const v = Number(row[f]);
        if (!isNaN(v) && v > 0) return v;
      }
      // try lowercase variants
      if (row[f.toLowerCase()] !== undefined && row[f.toLowerCase()] !== null) {
        const v2 = Number(row[f.toLowerCase()]);
        if (!isNaN(v2) && v2 > 0) return v2;
      }
    }
    return null;
  }

  // Compute 95% CI (in percentage points) from available fields or return null
  // Also check if the CI is suppressed (0–100) and update state
  function computeCIRange(row) {
    if (!row) return null;
    if (isCensusSite(row)) return null;
    const value = getPercentValue(row);
    if (value === null || value === undefined) return null;
    if (value === 100) return null;
    
    // Check if CI should be shown as a dash (missing/empty CI data)
    if (shouldShowCIDash(row)) return null;
    
    // Check for special suppression codes first
    if (shouldSuppressCI(row)) return null;
    
    // prefer explicit bounds if present
    const candidatesLower = [row.LowValue, row.LowerValue, parseConfidenceLower(row.ConfidenceInterval), row.LCI, row.Lower, row.LowCI];
    const candidatesUpper = [row.HighValue, row.UpperValue, parseConfidenceUpper(row.ConfidenceInterval), row.UCI, row.Upper, row.HighCI];
    let lower = null, upper = null;
    for (let c of candidatesLower) {
      if (c !== undefined && c !== null && String(c).trim() !== '') { lower = parseNumber(c); if (lower !== null && !isNaN(lower)) break; }
    }
    for (let c of candidatesUpper) {
      if (c !== undefined && c !== null && String(c).trim() !== '') { upper = parseNumber(c); if (upper !== null && !isNaN(upper)) break; }
    }
    if (lower !== null && upper !== null) {
      return { lower: Number(lower), upper: Number(upper) };
    }

    // Fallback: compute using proportion standard error p +/- 1.96*sqrt(p*(1-p)/n)
    const pct = parseNumber(row.NPercent) ?? parseNumber(row.DataValueRaw);
    const n = parseSampleSize(row);
    // If bounds are absent but the point estimate is exactly 0 or 100, treat as suppressed and show footnote
    if (pct === 0 || pct === 100) return null;
    if (pct === null || isNaN(pct)) return null;
    const p = pct / 100;
    if (n && n > 0) {
      const se = Math.sqrt(p * (1 - p) / n);
      const delta = 1.96 * se * 100; // convert back to percentage points
      const l = Math.max(0, (p * 100) - delta);
      const u = Math.min(100, (p * 100) + delta);
      
      return { lower: l, upper: u };
    }

    return null;
  }

  // derive local items for rendering the Local Surveys chart
  const allSourceForRender = Array.isArray(allLocationsData) ? allLocationsData : allLocationsData && allLocationsData.tableData ? allLocationsData.tableData : [];
  const localItemsForRender = allSourceForRender
    .filter((s) => (s.LocationType || "").toLowerCase() === "local")
    .filter((s) => !isSuppressedValue(s))
    // Hide locations with missing or exactly 0% values; always allow 100%
    .filter((s) => {
      const v = getPercentValue(s);
      return v !== null && v !== 0;
    });

  // Do not render the graph shell for All Locations when a subquestion is required
  // but not yet selected; this prevents the empty "No data available" block.
  const requiresSubQuestionSelection = isAll && question && question.SubQuestions && question.SubQuestions.length > 0 && !subQuestion;
  if (requiresSubQuestionSelection) {
    return (
      <ChartContainer>
      </ChartContainer>
    );
  }

  // For specific locations, check if the requested year has data.
  // If validation confirms no data, display "No data for selected location" message.
  if (!isAll && requestedYearHasData === false) {
    return (
      <ChartContainer>
        <div style={{ height: 100 }}>No data for selected location.</div>
      </ChartContainer>
    );
  }

  try {
    return (
      <>
      <ChartContainer>
        <div className="row">
          <div className="col-12 question-header__location-text">
            {/* SubQuestion header */}
            {(vizType === "graph" || !vizType) && subQuestion && (
              <h2
                className="mt-4 main-table-header"
                style={{
                  color: (question && question.TopicId && topicDictionary[question.TopicId] && topicDictionary[question.TopicId].color) || "#066194",
                  fontSize: "30px",
                }}
              >
                {subQuestion.SubQuestion}
              </h2>
            )}

            {/* DataVisualizationToggle for ALL locations */}
            {locationAbbr === "ALL" && (question.SubQuestions.length === 0 || subQuestion) && (
              <div className="mb-2 w-50 align-items-center mx-auto">
                <DataVisualizationToggle
                  onVizTypeChange={onVizTypeChange}
                  vizType={vizType}
                  question={question}
                  subQuestionValue={subQuestionValue}
                  locationAbbr={locationAbbr}
                  allowedOptions={["graph", "table", "map"]}
                />
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          {isAll && (
            <RadioGroup
              label="Value"
              name="all-mode"
              options={[{ value: "percent", label: "Percent" }, { value: "ci", label: "95% Confidence Interval" }]}
              value={allMode}
              onChange={(v) => {
                const selected = v === "ci" ? "95CI" : "PCT";
                selectGraphAllValue({ selected });
                setAllMode(v);
              }}
            />
          )}
          {(isState || isLocal) && (
            <RadioGroup
              label="Compare"
              name="compare-mode"
              options={isState ? [{ value: "None", label: "None" }, { value: "Nationwide", label: "Nationwide" }] : [{ value: "None", label: "None" }, { value: "State", label: "State" }, { value: "Nationwide", label: "Nationwide" }]}
              value={compareMode}
              onChange={(v) => {
                const selected = String(v || "None").toUpperCase();
                selectGraphCompare({ selected });
                setCompareMode(v);
              }}
            />
          )}
          {!isAll && compareNotice && (
            <div style={{ marginTop: 4, color: "#8a6d3b", fontSize: 13 }}>
              {compareNotice}
            </div>
          )}
        </div>
        {!isAll && (
          <div style={{ textAlign: "right", minWidth: 180 }}>
            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end" }}>
              <svg width="14" height="14" aria-hidden="true" focusable="false" style={{ marginRight: 8 }}>
                <rect x="0" y="0" width="14" height="14" fill={localColor} stroke="#000" strokeWidth="0.5" rx="2" ry="2" />
              </svg>
              <div style={{ fontWeight: 600 }}>{location ? location.LocationDesc : ""}</div>
              {compareMode && compareMode !== "None" && compareData && (
                <div style={{ display: "inline-flex", alignItems: "center", marginLeft: 12 }}>
                  <svg width="14" height="14" aria-hidden="true" focusable="false" style={{ marginRight: 8 }}>
                    <rect x="0" y="0" width="14" height="14" fill={getCompareColor()} stroke="#000" strokeWidth="0.5" rx="2" ry="2" />
                  </svg>
                  <div style={{ fontWeight: 400 }}>{(compareMode === "Nationwide") ? "Nationwide" : ((Array.isArray(compareData.data) ? (compareData.data[0] && (compareData.data[0].LocationTxt || compareData.data[0].LocationDesc)) : (compareData.data && compareData.data.tableData && compareData.data.tableData[0] && (compareData.data.tableData[0].LocationTxt || compareData.data.tableData[0].LocationDesc))) || compareData.abbr)}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isAll && (
          <div style={{ position: "relative", marginTop: 6, marginBottom: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 400, fontSize: 22, color: "#000" }}>State Surveys</div>
            <div style={{ fontWeight: 700, fontSize: 20 }}>{(location && location.LocationAbbr !== "ALL" && String(location.LocationDesc).trim().length > 0) ? location.LocationDesc : ""}</div>
          </div>
          <div style={{ position: "absolute", left: "218px", top: "2px", fontWeight: 300, fontSize: 20, color: "#000" }}>Range: {allRange ? `${allRange.min} - ${allRange.max}` : "N/A"}</div>
        </div>
      )}
        {isAll ? (
          <svg ref={svgRef} style={{ width: "100%", marginTop: 0, overflow: "visible" }} />
        ) : (
          // Do not render the main bordered container when we're showing
          // multiple per-subquestion charts (question has SubQuestions and
          // no single subQuestion is selected). Otherwise render the main
          // container only when we have single-pattern data to draw.
          !(question && question.SubQuestions && question.SubQuestions.length > 0 && !subQuestion) &&
          ((patternsData && ((Array.isArray(patternsData) && patternsData.length > 0) || (patternsData.tableData && patternsData.tableData.length > 0))) || (patternsMap && Object.keys(patternsMap).some((k) => {
            const d = patternsMap[k];
            const pd = Array.isArray(d) ? d : (d && d.tableData) || [];
            return pd && pd.length > 0;
          }))) ? (
            <div style={{ border: "1px solid #e6e6e6", borderRadius: 8, padding: 12, background: "#fff", marginTop: 8 }}>
              <svg ref={svgRef} style={{ width: "100%", overflow: "visible" }} />
            </div>
          ) : null
        )}

        {/* When a specific location is selected and multiple subquestions exist,
            render one chart per subquestion into its own SVG. */}
        {!isAll && question && question.SubQuestions && question.SubQuestions.length > 0 && !subQuestion && (
          // Only render boxes for subquestions that have fetched pattern data in patternsMap
          question.SubQuestions.filter((sq) => {
            const d = patternsMap && patternsMap[sq.SubQuestionId];
            if (!d) return false;
            const pd = Array.isArray(d) ? d : (d.tableData || []);
            return pd && pd.length > 0;
          }).map((sq, idx) => {
            // We need the original index to keep refs aligned - find index in SubQuestions
            const originalIdx = question.SubQuestions.findIndex((q) => q.SubQuestionId === sq.SubQuestionId);
            return (
              <div key={sq.SubQuestionId || originalIdx} style={{ position: "relative", marginTop: 18, marginBottom: 6, border: "1px solid #e6e6e6", borderRadius: 8, padding: 12, background: "#fff" }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6, whiteSpace: "normal", wordBreak: "break-word" }}>{sq.SubQuestion}</div>
                  <svg ref={(el) => (svgPatternRefs.current[originalIdx] = el)} style={{ width: "100%", marginTop: 8, overflow: "visible" }} />
              </div>
            );
          })
        )}

      {isAll && localItemsForRender && localItemsForRender.length > 0 && (
        <>
          <div style={{ position: "relative", marginTop: 18, marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontWeight: 400, fontSize: 22, color: "#000" }}>Local Survey</div>
              <div style={{ fontWeight: 700, fontSize: 20 }} />
            </div>
            <div style={{ position: "absolute", left: "218px", top: "2px", fontWeight: 300, fontSize: 20, color: "#000" }}>Range: {localRange ? `${localRange.min} - ${localRange.max}` : "N/A"}</div>
          </div>
          <svg ref={svgLocalRef} style={{ width: "100%", marginTop: 0, overflow: "visible" }} />
        </>
      )}

      </ChartContainer>
      </>
    );
  } catch (e) {
    // Log detailed diagnostic info to help root-cause the render error
    // Do not rethrow so the rest of the app remains usable.
    try {
      // Provide concise shape info to avoid huge console dumps
      const payloadSummary = {
        isAll: !!isAll,
        isState: !!isState,
        isLocal: !!isLocal,
        subQuestion: !!subQuestion,
        allLocationsDataType: Array.isArray(allLocationsData) ? 'array' : (allLocationsData && allLocationsData.tableData ? 'object.tableData' : typeof allLocationsData),
        patternsDataType: Array.isArray(patternsData) ? 'array' : (patternsData && patternsData.tableData ? 'object.tableData' : typeof patternsData),
        compareDataType: compareData ? (Array.isArray(compareData.data) ? 'compare.array' : (compareData.data && compareData.data.tableData ? 'compare.object.tableData' : typeof compareData.data)) : 'none',
      };
      // eslint-disable-next-line no-console
      console.error('Graph render error', e, payloadSummary);
    } catch (e2) {
      // eslint-disable-next-line no-console
      console.error('Graph render error (failed to log payload)', e, e2);
    }
    return (
      <ChartContainer>
        <div style={{ color: "#a00", padding: 12, fontWeight: 600 }}>Error rendering graph — see console for details.</div>
      </ChartContainer>
    );
  }
};

export default Graph;